import React, { useState, useEffect, useRef } from "react";
import {
  FaShoppingCart,
  FaBoxOpen,
  FaCheckCircle,
  FaSearch,
  FaHistory,
  FaTimes,
  FaArrowLeft,
  FaTimesCircle,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import NoImage from "../assets/images/no-image.jpg";
import { db, functions } from "./app/protected/firebase";
import {
  collection,
  serverTimestamp,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  runTransaction,
  onSnapshot
} from "firebase/firestore";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { useParams, useSearchParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Add heartbeat animation
const style = document.createElement('style');
style.textContent = `
  @keyframes heartbeat {
    0%, 100% { transform: scale(1); }
    25% { transform: scale(1.1); }
    50% { transform: scale(1); }
  }
`;
document.head.appendChild(style);

const ANY_VARIANT_ID = "__ANY_VARIANT__";

const OnlineOrdering = () => {
  const { companyId } = useParams();
  const [currentPage, setCurrentPage] = useState(1); // 1: Welcome, 2: Menu, 3: Order Confirmation
  const [cart, setCart] = useState([]);
  const [itemNote, setItemNote] = useState({}); // Store notes for each cart item
  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    phone: "",
    fulfillmentType: "pickup",
    pickupOption: "now",
    pickupDate: "",
    pickupTime: "",
    deliveryAddress: "",
    orderType: "", // "dineIn" or "takeAway"
  });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [placedOrder, setPlacedOrder] = useState(null);
  const [discountOptions, setDiscountOptions] = useState([]);
  const [discountCodeInput, setDiscountCodeInput] = useState('');
  const [discountError, setDiscountError] = useState('');
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [appliedCode, setAppliedCode] = useState('');
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [companyName, setCompanyName] = useState("Restaurant");
  const [companyLogo, setCompanyLogo] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [showMobileCheckout, setShowMobileCheckout] = useState(false);
  const [queueNumber, setQueueNumber] = useState(null);
  const [showCancelButton, setShowCancelButton] = useState(true);
  const [orderHistory, setOrderHistory] = useState([]);
  const [showOrderHistory, setShowOrderHistory] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [phoneError, setPhoneError] = useState("");
  const [showPhonePrompt, setShowPhonePrompt] = useState(false);
  const [orderHistoryList, setOrderHistoryList] = useState([]);
  const [showOrderHistoryModal, setShowOrderHistoryModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalData, setConfirmModalData] = useState(null);
  const [privacyError, setPrivacyError] = useState(false);
  const [showOrderDetailsModal, setShowOrderDetailsModal] = useState(false);
  const [phonePromptValue, setPhonePromptValue] = useState("+60");
  const [products, setProducts] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [selectedType, setSelectedType] = useState("All");
  const [isSubmittingNumber, setIsSubmittingNumber] = useState(false);
  const [cancelCountdown, setCancelCountdown] = useState(15);
  const [uiHydrated, setUiHydrated] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("qr");
  const [payAtCounterEnabled, setPayAtCounterEnabled] = useState(true);
  const [stripePaymentEnabled, setStripePaymentEnabled] = useState(false);
  const [pickupEnabled, setPickupEnabled] = useState(true);
  const [deliveryEnabled, setDeliveryEnabled] = useState(false);
  const [duitnowQRUrl, setDuitnowQRUrl] = useState("");
  const [paymentProofFile, setPaymentProofFile] = useState(null);
  const [paymentProofError, setPaymentProofError] = useState("");
  const [isPaying, setIsPaying] = useState(false);
  const [isUploadingPaymentProof, setIsUploadingPaymentProof] = useState(false);
  const [cancelDeadline, setCancelDeadline] = useState(null);
  const paymentHandledRef = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const uiStateKey = 'onlineOrderingUI';
  const placedOrderKey = 'onlineOrderingPlacedOrder';
  const cancelDeadlineKey = 'onlineOrderingCancelDeadline';
  const storage = getStorage();

  useEffect(() => {
    const savedCart = sessionStorage.getItem('onlineOrderingCart');
    const savedNotes = sessionStorage.getItem('onlineOrderingNotes');
    const savedCustomer = sessionStorage.getItem('onlineOrderingCustomerInfo');
    const savedUI = sessionStorage.getItem(uiStateKey);
    const savedPlacedOrder = sessionStorage.getItem(placedOrderKey);
    const savedCancelDeadline = sessionStorage.getItem(cancelDeadlineKey);

    if (savedCart) {
      try {
        setCart(JSON.parse(savedCart));
      } catch (err) {
        console.warn('Failed to parse saved cart', err);
      }
    }
    if (savedNotes) {
      try {
        setItemNote(JSON.parse(savedNotes));
      } catch (err) {
        console.warn('Failed to parse saved notes', err);
      }
    }
    if (savedCustomer) {
      try {
        setCustomerInfo((prev) => ({ ...prev, ...JSON.parse(savedCustomer) }));
      } catch (err) {
        console.warn('Failed to parse saved customer info', err);
      }
    }
    if (savedPlacedOrder) {
      try {
        setPlacedOrder(JSON.parse(savedPlacedOrder));
      } catch (err) {
        console.warn('Failed to parse saved placed order', err);
      }
    }
    if (savedUI) {
      try {
        const parsed = JSON.parse(savedUI);
        if (typeof parsed.currentPage === 'number') setCurrentPage(parsed.currentPage);
        if (typeof parsed.activeCategory === 'string') setActiveCategory(parsed.activeCategory);
        if (typeof parsed.searchTerm === 'string') setSearchTerm(parsed.searchTerm);
        if (typeof parsed.selectedType === 'string') setSelectedType(parsed.selectedType);
        if (typeof parsed.showMobileCheckout === 'boolean') setShowMobileCheckout(parsed.showMobileCheckout);
        if (typeof parsed.privacyAccepted === 'boolean') setPrivacyAccepted(parsed.privacyAccepted);
        if (typeof parsed.selectedPaymentMethod === 'string') {
          setSelectedPaymentMethod(parsed.selectedPaymentMethod === 'counter' ? 'qr' : parsed.selectedPaymentMethod);
        }
      } catch (err) {
        console.warn('Failed to parse saved UI state', err);
      }
    } else if (savedPlacedOrder) {
      setCurrentPage(3);
    }
    if (savedCancelDeadline) {
      const parsed = Number(savedCancelDeadline);
      if (Number.isFinite(parsed)) {
        setCancelDeadline(parsed);
      }
    }
    setUiHydrated(true);
  }, []);

  // Save cart data to session storage whenever cart changes
  useEffect(() => {
    if (cart.length > 0) {
      sessionStorage.setItem('onlineOrderingCart', JSON.stringify(cart));
    } else {
      sessionStorage.removeItem('onlineOrderingCart');
    }
  }, [cart]);

  // Save item notes to session storage whenever they change
  useEffect(() => {
    if (Object.keys(itemNote).length > 0) {
      sessionStorage.setItem('onlineOrderingNotes', JSON.stringify(itemNote));
    } else {
      sessionStorage.removeItem('onlineOrderingNotes');
    }
  }, [itemNote]);

  // Save customer info to session storage whenever it changes
  useEffect(() => {
    const {
      name,
      phone,
      fulfillmentType,
      pickupOption,
      pickupDate,
      pickupTime,
      deliveryAddress,
      orderType,
    } = customerInfo;
    const hasData = name || phone || pickupDate || pickupTime || deliveryAddress || orderType;
    
    if (hasData) {
      sessionStorage.setItem('onlineOrderingCustomerInfo', JSON.stringify({
        name,
        phone,
        fulfillmentType,
        pickupOption,
        pickupDate,
        pickupTime,
        deliveryAddress,
        orderType,
      }));
    } else {
      sessionStorage.removeItem('onlineOrderingCustomerInfo');
    }
  }, [customerInfo]);

  useEffect(() => {
    if (!uiHydrated) return;
    const nextUI = {
      currentPage,
      activeCategory,
      searchTerm,
      selectedType,
      showMobileCheckout,
      privacyAccepted,
      selectedPaymentMethod,
    };
    sessionStorage.setItem(uiStateKey, JSON.stringify(nextUI));
  }, [currentPage, activeCategory, searchTerm, selectedType, showMobileCheckout, privacyAccepted, selectedPaymentMethod]);

  useEffect(() => {
    if (placedOrder) {
      sessionStorage.setItem(placedOrderKey, JSON.stringify(placedOrder));
      return;
    }
    sessionStorage.removeItem(placedOrderKey);
  }, [placedOrder]);

  useEffect(() => {
    if (!cancelDeadline || currentPage !== 3 || !showCancelButton) return;
    const tick = () => {
      const remainingMs = cancelDeadline - Date.now();
      const remaining = Math.max(0, Math.ceil(remainingMs / 1000));
      setCancelCountdown(remaining);
      if (remaining <= 0) {
        setShowCancelButton(false);
        setCancelDeadline(null);
        sessionStorage.removeItem(cancelDeadlineKey);
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [cancelDeadline, currentPage, showCancelButton]);

  useEffect(() => {
    if (selectedPaymentMethod === "stripe" && !stripePaymentEnabled) {
      setSelectedPaymentMethod(payAtCounterEnabled ? "qr" : "stripe");
    }
    if (selectedPaymentMethod === "qr" && !payAtCounterEnabled) {
      setSelectedPaymentMethod(stripePaymentEnabled ? "stripe" : "qr");
    }
  }, [payAtCounterEnabled, stripePaymentEnabled, selectedPaymentMethod]);

  useEffect(() => {
    if (!pickupEnabled && deliveryEnabled && customerInfo.fulfillmentType === "pickup") {
      setCustomerInfo((prev) => ({
        ...prev,
        fulfillmentType: "delivery",
        orderType: "delivery",
      }));
    }
    if (!deliveryEnabled && pickupEnabled && customerInfo.fulfillmentType === "delivery") {
      setCustomerInfo((prev) => ({
        ...prev,
        fulfillmentType: "pickup",
        deliveryAddress: "",
        orderType: prev.orderType === "delivery" ? "" : prev.orderType,
      }));
    }
  }, [pickupEnabled, deliveryEnabled, customerInfo.fulfillmentType]);

  const paymentOrderId = searchParams.get('orderId');
  const paymentSessionId = searchParams.get('session_id');
  const paymentResult = searchParams.get('payment');

  useEffect(() => {
    if (!companyId || !paymentOrderId || paymentHandledRef.current) return;
    paymentHandledRef.current = true;

    const handlePaymentReturn = async () => {
      try {
        if (!placedOrder || placedOrder.orderId !== paymentOrderId) {
          const orderRef = doc(db, "companies", companyId, "preorders", paymentOrderId);
          const orderSnap = await getDoc(orderRef);
          if (orderSnap.exists()) {
            const data = orderSnap.data();
            setPlacedOrder({ ...data, orderId: paymentOrderId });
            setCustomerInfo({
              name: data.customerName || data.customerInfo?.name || "",
              phone: data.customerPhone || data.customerInfo?.phone || "",
              fulfillmentType: data.fulfillmentType || data.customerInfo?.fulfillmentType || "pickup",
              pickupOption: data.pickupOption || data.customerInfo?.pickupOption || "now",
              pickupDate: data.pickupDate || data.customerInfo?.pickupDate || "",
              pickupTime: data.pickupTime || data.customerInfo?.pickupTime || "",
              deliveryAddress: data.deliveryAddress || data.customerInfo?.deliveryAddress || "",
              orderType: data.orderType || data.customerInfo?.orderType || "",
            });
            setCurrentPage(3);
          }
        }

        if (paymentResult === 'success' && paymentSessionId) {
          const verifyPayment = httpsCallable(functions, "verifyOnlineOrderCheckoutSession");
          const { data } = await verifyPayment({
            companyId,
            orderId: paymentOrderId,
            sessionId: paymentSessionId,
          });
          if (data?.paymentStatus === 'paid') {
            toast.success("Payment successful!");
          } else {
            toast.error("Payment not completed. You can try again or switch to Pay with QR.");
          }
        } else if (paymentResult === 'cancel') {
          toast.info("Payment cancelled. You can retry online payment or switch to Pay with QR.");
        }
      } catch (err) {
        console.error("Payment verification failed:", err);
        toast.error("Unable to verify payment. Please try again.");
      } finally {
        setSearchParams({}, { replace: true });
      }
    };

    handlePaymentReturn();
  }, [companyId, paymentOrderId, paymentResult, paymentSessionId, placedOrder, setSearchParams]);

  // Clear session storage when order is successfully placed
  useEffect(() => {
    if (placedOrder) {
      sessionStorage.removeItem('onlineOrderingCart');
      sessionStorage.removeItem('onlineOrderingNotes');
      sessionStorage.removeItem('onlineOrderingCustomerInfo');
    }
  }, [placedOrder]);

  // Function to clear all saved data
  const clearSavedData = () => {
    sessionStorage.removeItem('onlineOrderingCart');
    sessionStorage.removeItem('onlineOrderingNotes');
    sessionStorage.removeItem('onlineOrderingCustomerInfo');
    sessionStorage.removeItem(uiStateKey);
    sessionStorage.removeItem(placedOrderKey);
    sessionStorage.removeItem(cancelDeadlineKey);
    setCart([]);
    setItemNote({});
    setCustomerInfo({
      name: "",
      phone: "",
      fulfillmentType: "pickup",
      pickupOption: "now",
      pickupDate: "",
      pickupTime: "",
      deliveryAddress: "",
      orderType: "",
    });
    setSelectedPaymentMethod("qr");
    setPrivacyAccepted(false);
    setCancelDeadline(null);
    setDiscountCodeInput('');
    setDiscountError('');
    setSelectedDiscount(null);
    setAppliedCode('');
    setPaymentProofFile(null);
    setPaymentProofError('');
  };
  const [nameError, setNameError] = useState("");
  const [pickupDateError, setPickupDateError] = useState("");
  const [pickupTimeError, setPickupTimeError] = useState("");
  const [isCancelling, setIsCancelling] = useState(false);
  const [orderExists, setOrderExists] = useState(true);
  const [onlineOrderingEnabled, setOnlineOrderingEnabled] = useState(true);
  const [preOrderEnabled, setPreOrderEnabled] = useState(false);
  const [stallType, setStallType] = useState(null);
  const [publicId, setPublicId] = useState(null);
  const mode = customerInfo.fulfillmentType === "delivery" ? "DELIVERY" : "PICKUP";
  const availableFulfillmentCount = Number(pickupEnabled) + Number(deliveryEnabled);
  const canRenderCancelButton =
    showCancelButton &&
    Number.isFinite(Number(cancelDeadline)) &&
    Number(cancelDeadline) > Date.now();
  const [taxSettings, setTaxSettings] = useState({
      serviceTax10Enabled: false,
      serviceTax10Rate: 10,
      sst6Enabled: false,
      sst8Enabled: false,
      serviceTax10Label: "Service Tax",
      sst6Label: "SST (6%)",
      sst8Label: "SST (8%)"
    });
  const [openingTime, setOpeningTime] = useState("");
  const [closingTime, setClosingTime] = useState("");
  const [defaultOpeningTime, setDefaultOpeningTime] = useState("");
  const [defaultClosingTime, setDefaultClosingTime] = useState("");
  const [onlineOrderingSchedule, setOnlineOrderingSchedule] = useState(null);
  const [isWithinOperatingHours, setIsWithinOperatingHours] = useState(true);
  const [scheduleEnabledToday, setScheduleEnabledToday] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProductOptionValues, setSelectedProductOptionValues] = useState({});
  const [selectedProductQuantity, setSelectedProductQuantity] = useState(1);
  const [productImageIndex, setProductImageIndex] = useState(0);
  const [bundleVariantModalOpen, setBundleVariantModalOpen] = useState(false);
  const [bundleVariantContext, setBundleVariantContext] = useState({ bundle: null, selections: {} });
  const [bundleDraftSelections, setBundleDraftSelections] = useState({});
  const onlineOrderingLive = onlineOrderingEnabled && scheduleEnabledToday && isWithinOperatingHours;
  const getNoteKey = (item) =>
    item?.optionsKey ? `${item.id}::${item.optionsKey}` : item?.id;

  const formatVariantName = (product, variantId, fallback = "") => {
    if (!variantId) return fallback;
    const variant = product?.variants?.find(
      (v) => v.id === variantId || v.variantId === variantId
    );
    if (!variant) return fallback;
    const name =
      (variant.options || [])
        .map((o) => `${o.group}: ${o.value}`)
        .join(" / ") || variant.name || variant.label;
    return name || fallback;
  };
  const getVariantLabel = (variant) =>
    (variant?.options || []).map((o) => `${o.group}: ${o.value}`).join(" / ") ||
    variant?.name ||
    variant?.label ||
    "";
  const buildAddressString = (profile = {}) => {
    if (profile.fullAddress) return profile.fullAddress;

    const collect = [];
    const pushVal = (val) => {
      if (!val) return;
      if (typeof val === "string") {
        collect.push(val);
        return;
      }
      if (typeof val === "object") {
        Object.values(val).forEach(pushVal);
      }
    };

    // Prefer structured fields in order, but also flatten any object stored in address
    pushVal(profile.street);
    pushVal(profile.postcode);
    pushVal(profile.city);
    pushVal(profile.state);
    pushVal(profile.country);

    // Fallback: if address is an object with nested props
    if (typeof profile.address === "object") {
      pushVal(profile.address);
    }

    return collect.filter(Boolean).join(", ");
  };
  const parseTimeToMinutes = (timeStr) => {
    if (!timeStr || typeof timeStr !== "string") return null;
    const [h, m] = timeStr.split(":").map((v) => parseInt(v, 10));
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return h * 60 + m;
  };

  const minutesToTimeString = (mins) => {
    if (mins === null || mins === undefined) return undefined;
    const clamped = Math.max(0, Math.min(mins, 23 * 60 + 59));
    const h = String(Math.floor(clamped / 60)).padStart(2, "0");
    const m = String(clamped % 60).padStart(2, "0");
    return `${h}:${m}`;
  };

  const getTodayStr = () => currentTime.toISOString().split("T")[0];
  const getDateStrDaysFromToday = (days) => {
    const d = new Date(currentTime);
    d.setDate(d.getDate() + days);
    return d.toISOString().split("T")[0];
  };
  const getNowTimeStr = () =>
    currentTime.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  const offlineReason = !onlineOrderingEnabled
    ? "Online ordering is disabled by the store."
    : !scheduleEnabledToday
      ? "Online ordering is not available today."
    : !isWithinOperatingHours
      ? `Online ordering is available between ${openingTime || "--:--"} and ${closingTime || "--:--"}.`
      : "";

  const getScheduleForDate = (dateStr) => {
    if (!dateStr) return { enabled: true, open: defaultOpeningTime, close: defaultClosingTime };
    const dayKey = ['sun','mon','tue','wed','thu','fri','sat'][new Date(dateStr).getDay()];
    const daySchedule = onlineOrderingSchedule?.[dayKey];
    return {
      enabled: daySchedule ? daySchedule.enabled !== false : true,
      open: daySchedule?.open || defaultOpeningTime,
      close: daySchedule?.close || defaultClosingTime,
    };
  };

  const isTimeWithinStoreHours = (timeStr, schedule) => {
    const selected = parseTimeToMinutes(timeStr);
    const open = parseTimeToMinutes(schedule?.open);
    const close = parseTimeToMinutes(schedule?.close);
    const openEffective = open !== null ? open + 30 : null;
    const closeEffective = close !== null ? close - 30 : null;
    if (selected === null) return true; // no time to compare
    if (openEffective === null || closeEffective === null) return true; // hours not set, allow

    if (openEffective <= closeEffective) {
      return selected >= openEffective && selected <= closeEffective;
    }

    // Overnight hours (e.g., 18:00 - 02:00)
    return selected >= openEffective || selected <= closeEffective;
  };

  const getPickupTimeBounds = (dateStr, schedule) => {
    const today = getTodayStr();
    const nowStr = getNowTimeStr();
    const open = parseTimeToMinutes(schedule?.open);
    const close = parseTimeToMinutes(schedule?.close);
    const openEffective = open !== null ? open + 30 : null;
    const closeEffective = close !== null ? close - 30 : null;
    const nowMinutes = parseTimeToMinutes(nowStr);

    // Avoid conflicting min/max when store uses overnight hours by skipping bounds
    if (openEffective !== null && closeEffective !== null && openEffective > closeEffective) {
      return { min: undefined, max: undefined };
    }

    let min;
    if (dateStr === today) {
      const candidates = [openEffective, nowMinutes].filter((v) => v !== null);
      if (candidates.length) {
        min = minutesToTimeString(Math.max(...candidates));
      }
    } else if (openEffective !== null) {
      min = minutesToTimeString(openEffective);
    }

    const max = closeEffective !== null ? minutesToTimeString(closeEffective) : undefined;
    return { min, max };
  };


  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const dayKey = ['sun','mon','tue','wed','thu','fri','sat'][currentTime.getDay()];
    const daySchedule = onlineOrderingSchedule?.[dayKey];
    setScheduleEnabledToday(daySchedule?.enabled !== false);
    setOpeningTime(daySchedule?.open || defaultOpeningTime);
    setClosingTime(daySchedule?.close || defaultClosingTime);
  }, [currentTime, onlineOrderingSchedule, defaultOpeningTime, defaultClosingTime]);

  useEffect(() => {
    const openMinutes = parseTimeToMinutes(openingTime);
    const closeMinutes = parseTimeToMinutes(closingTime);

    if (openMinutes === null || closeMinutes === null) {
      setIsWithinOperatingHours(true);
      return;
    }

    const nowMinutes = currentTime.getHours() * 60 + currentTime.getMinutes();
    const within =
      openMinutes <= closeMinutes
        ? nowMinutes >= openMinutes && nowMinutes < closeMinutes
        : nowMinutes >= openMinutes || nowMinutes < closeMinutes; // overnight support

    setIsWithinOperatingHours(within);
  }, [currentTime, openingTime, closingTime]);

  useEffect(() => {
    let unsubProducts = null;
    let unsubBundles = null;

    const fetchData = async () => {
      if (!companyId) {
        console.error("No company ID provided");
        setIsLoading(false);
        return;
      }

      try {
        // 🔹 Fetch company name and logo
        const companyProfileRef = doc(db, "companies", companyId, "companyProfile", "info");
        const companyProfileSnap = await getDoc(companyProfileRef);

        if (companyProfileSnap.exists()) {
          const data = companyProfileSnap.data();
          setCompanyName(data?.companyName);
          setCompanyLogo(data?.logoUrl || "");
          setCompanyAddress(buildAddressString(data));
        }

        const companyDoc = await getDoc(doc(db, "companies", companyId));
        if (companyDoc.exists()) {
          setPublicId(companyDoc.data()?.publicId || null);
        }

        // 🔹 Check if online ordering is enabled and fetch tax settings
        const settingsRef = doc(db, "companies", companyId, "companySettings", "info");
        const settingsSnap = await getDoc(settingsRef);
        if (settingsSnap.exists()) {
          const settings = settingsSnap.data();
          setOnlineOrderingEnabled(settings.onlineOrderingEnabled || false);
          setPreOrderEnabled(!!settings.enablePreOrders);
          setPickupEnabled(settings.enablePickup !== false);
          setDeliveryEnabled(!!settings.enableDelivery);
          setOnlineOrderingSchedule(settings.onlineOrderingSchedule || null);
          setDefaultOpeningTime(settings.onlineOrderingOpeningTime || settings.openingTime || "");
          setDefaultClosingTime(settings.onlineOrderingClosingTime || settings.closingTime || "");
          const counterEnabled = settings.payAtCounterEnabled !== false;
          const stripeEnabled = !!settings.stripePaymentEnabled && !!settings.stripeKeyConfigured;
          setDuitnowQRUrl(settings.duitnowQRUrl || "");
          setPayAtCounterEnabled(counterEnabled);
          setStripePaymentEnabled(stripeEnabled);
          if (!stripeEnabled && selectedPaymentMethod === "stripe") {
            setSelectedPaymentMethod(counterEnabled ? "qr" : "stripe");
          }
          if (!counterEnabled && stripeEnabled) {
            setSelectedPaymentMethod("stripe");
          }
          setTaxSettings({
            serviceTax10Enabled: settings.serviceTax10Enabled || false,
            serviceTax10Rate: settings.serviceTax10Rate ?? 10,
            sst6Enabled: settings.sst6Enabled || false,
            sst8Enabled: settings.sst8Enabled || false,
            serviceTax10Label: settings.serviceTax10Label || "Service Tax",
            sst6Label: settings.sst6Label || "SST (6%)",
            sst8Label: settings.sst8Label || "SST (8%)"
          });
        }

        if (settingsSnap.exists()) {
          const settings = settingsSnap.data();
          setStallType(settings.stallType);
        }

        const discountsSnap = await getDocs(
          collection(db, "companies", companyId, "companySettings", "discounts", "data")
        );
        setDiscountOptions(discountsSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));

        // 🔹 Prepare refs
        const productsRef = collection(db, "companies", companyId, "inventory", "data", "products");
        const bundlesRef = collection(db, "companies", companyId, "inventory", "data", "bundles");

        // 🔹 Real-time listener for products (filter by onlineOrdering)
        unsubProducts = onSnapshot(productsRef, (snapshot) => {
          const fetchedProducts = snapshot.docs
            .map((doc) => {
              const data = doc.data();
              const firstImage = Array.isArray(data.images) && data.images.length ? data.images[0] : null;
              return {
                id: doc.id,
                ...data,
                imageUrl: data.image || firstImage || "",
                inStock: typeof data.qty === "number" ? data.qty > 0 : true,
              };
            })
            .filter(product => {
              // Only show if explicitly true OR undefined (default for old items)
              // Hide if explicitly false
              return product.onlineOrdering === true;
            });
          setProducts(fetchedProducts);
        });

        // 🔹 Real-time listener for bundles (filter by onlineOrdering)
        const unsubBundles = onSnapshot(bundlesRef, (snapshot) => {
        const fetchedBundles = snapshot.docs
          .map((doc) => {
            const data = doc.data();
            const normalizeImage = (img) => {
              if (!img) return null;
              if (typeof img === "string") return img;
              if (typeof img === "object") return img.url || img.downloadURL || img.src || null;
              return null;
            };
            const images = (Array.isArray(data.images) ? data.images : data.image ? [data.image] : [])
              .map(normalizeImage)
              .filter(Boolean);
            const firstImage = images.length ? images[0] : null;
            // Use 'bundleQty' to store the potential max quantity of the bundle itself
            const bundleStock = Number(data.bundleQty ?? data.quantity ?? Infinity); // Check for a dedicated 'bundleQty' or 'quantity' field
            return {
              id: doc.id,
              ...data,
              type: "bundle",
              images,
              imageUrl: data.imageUrl || firstImage || "",
              items: Array.isArray(data.items)
                ? data.items.map((it) => ({
                    ...it,
                    allowVariantChoice: it.allowVariantChoice || it.variantId === ANY_VARIANT_ID,
                  }))
                : [],
              // Store the bundle's max stock separately (use Infinity if not quantity-controlled)
              bundleMaxQty: bundleStock,
               // This inStock check will be handled dynamically later
              inStock: bundleStock > 0, // Fallback check, will be refined in rendering logic
            };
          })
          .filter(bundle => {
            // Only show if explicitly true OR undefined (default for old items)
            // Hide if explicitly false
            return bundle.onlineOrdering === true;
          });
        setBundles(fetchedBundles);
      });
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load data. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();

    // ✅ Cleanup on unmount
    return () => {
      if (typeof unsubProducts === "function") unsubProducts();
      if (typeof unsubBundles === "function") unsubBundles();
    };
  }, [companyId]);

  useEffect(() => {
    const fetchProductTypes = async () => {
      try {
        const productTypesRef = collection(
          db,
          "companies",
          companyId,
          "companySettings",
          "inventory",
          "productTypes"
        );
        const snap = await getDocs(productTypesRef);
        const types = snap.docs.map((doc) => doc.data()?.name).filter(Boolean);
        setProductTypes(types);
      } catch (error) {
        console.error("Error fetching product types:", error);
        setProductTypes([]);
      }
    };

    if (companyId) {
      fetchProductTypes();
    }
  }, [companyId]);


  // Combine both products and bundles (both already filtered by onlineOrdering === true)
  const combinedItems = [...products, ...bundles];

  // build quick lookup map for product stocks
  const productsMap = React.useMemo(() => {
    const m = {};
    for (const p of products) {
      m[p.id] = p;
    }
    return m;
  }, [products]);


// Apply search and category filters
  const filteredItems = combinedItems.filter((item) => {
    const st = searchTerm.trim().toLowerCase();

    const matchesSearch =
      !st ||
      item.name?.toLowerCase().includes(st) ||
      item.description?.toLowerCase().includes(st);

    // Category filter logic
    const matchesCategory =
      activeCategory === "All" ||
      item.type === activeCategory ||
      (activeCategory === "bundle" && item.type === "bundle");

    return matchesSearch && matchesCategory;
  });

  // Helper function to get items by type
  const getItemsByType = (type) => {
    return filteredItems.filter((item) => item.type === type);
  };

  // Return how many units of a productId are already reserved in cart
  const getReservedCountForProduct = (productId, cart, variantId = null) => {
    let reserved = 0;
    for (const ci of cart) {
      // 1. Check if the cart item is the single product we're looking for
      // Check type is NOT bundle AND ID matches. Default to 'product' type if not defined.
      const itemType = ci.type || 'product';
      if (itemType !== 'bundle' && ci.id === productId) {
        if (variantId) {
          if ((ci.variantId || null) === variantId) reserved += Number(ci.quantity || ci.qty || 0);
        } else {
          reserved += Number(ci.quantity || ci.qty || 0);
        }
      }

      // 2. Check if the cart item is a bundle that CONTAINS the product
      if (itemType === 'bundle' && Array.isArray(ci.items)) {
        for (const sub of ci.items) {
          if (sub.id === productId) {
            if (variantId && (sub.variantId || null) !== variantId) continue;
            // sub.quantity = how many of that product per bundle
            // ci.quantity = how many of the whole bundle are in the cart
            reserved += Number(sub.quantity || 1) * Number(ci.quantity || ci.qty || 0);
          }
        }
      }
    }
    return reserved;
  };

  // Compute how many bundles can still be added given current product stocks & cart reservations
  const getAvailableBundleCount = (bundleItem, productsMap, cart) => {
     if (!Array.isArray(bundleItem.items) || bundleItem.items.length === 0) return 0;
    
     // 1. Determine the limit imposed by the bundle's own quantity field (if set)
     // Use bundleMaxQty from the fetched data (defaults to Infinity if not set)
     const bundleLimit = Number(bundleItem.bundleMaxQty ?? Infinity);
     if (bundleLimit <= 0) return 0;

     // 2. Determine the limit imposed by the bundle's required sub-products
     const availPerSub = bundleItem.items.map((sub) => {
       const product = productsMap[sub.id];
       const totalVariantQty = Array.isArray(product?.variants)
         ? product.variants.reduce((s, v) => s + Number(v.qty || 0), 0)
         : 0;
       const variant = sub.variantId && sub.variantId !== ANY_VARIANT_ID
         ? product?.variants?.find(
             (v) => v.id === sub.variantId || v.variantId === sub.variantId
           )
         : null;
       const productQty = sub.variantId === ANY_VARIANT_ID || sub.allowVariantChoice
         ? Number(product?.qty ?? totalVariantQty ?? 0)
         : variant
           ? Number(variant.qty ?? 0)
           : Number(product?.qty ?? product?.data?.qty ?? 0);
       const reserved = getReservedCountForProduct(
         sub.id,
         cart,
         sub.variantId === ANY_VARIANT_ID || sub.allowVariantChoice
           ? null
           : variant
             ? (variant.id || variant.variantId)
             : null
       );

       const remaining = productQty - reserved;
       const neededPerBundle = Number(sub.quantity || 1);

       if (remaining <= 0) return 0;
       return Math.floor(remaining / neededPerBundle);
     });

     // 3. The true limit is the minimum of (sub-product constraint, bundle's max qty)
     const subProductLimit = Math.min(...availPerSub);

     return Math.min(subProductLimit, bundleLimit);
  };

  // Compute available units for a product (considering reserved counts by bundles + singles)
  const getAvailableProductCount = (productId, productQty, cart, variantId = null) => {
    const reserved = getReservedCountForProduct(productId, cart, variantId);
    return Math.max(0, Number(productQty || 0) - reserved);
  };

  // returns how many of this item are currently in the cart (0 if none)
  const getCurrentInCart = (itemId) => {
    const ci = cart.find(c => c.id === itemId);
    return ci ? Number(ci.quantity || ci.qty || 0) : 0;
  };

  // returns true if the item should be considered out of stock (given current cart)
  const isItemOutOfStock = (item) => {
    if (!item) return true;

    if (item.type === 'bundle') {
      const available = getAvailableBundleCount(item, productsMap, cart);
      const currentInCart = getCurrentInCart(item.id);
      // Use same logic as updateQuantity: available + currentInCart
      return currentInCart >= (available + currentInCart);
    } else {
      const prod = productsMap[item.id] || item;
      const variantAvail = getVariantAvailable(item, item.selectedOptions?.length ? null : {});
      const available = variantAvail !== null
        ? variantAvail
        : getAvailableProductCount(item.id, prod?.qty ?? item.qty ?? 0, cart, item.variantId || null);
      const currentInCart = getCurrentInCart(item.id);
      // Use same logic as updateQuantity: available + currentInCart
      return currentInCart >= (available + currentInCart);
    }
  };

  // label for the Add button
  const getAddButtonLabel = (item) => {
    return isItemOutOfStock(item) ? 'Out of stock' : 'Add to Order';
  };

  const getOptionKey = (opt, idx) =>
    (typeof opt === "string" || typeof opt === "number")
      ? String(opt)
      : opt?.id || opt?.value || opt?.name || opt?.label || `opt-${idx}`;

  const getValueKey = (val, idx) =>
    (typeof val === "string" || typeof val === "number")
      ? String(val)
      : val?.id || val?.value || val?.name || val?.label || val?.title || val?.text || `val-${idx}`;

  const getValueLabel = (val, idx) =>
    (typeof val === "string" || typeof val === "number")
      ? String(val)
      : val?.name || val?.label || val?.value || val?.title || val?.text || `Option ${idx + 1}`;

  const getOptionPrice = (opt) => Number(opt?.price || opt?.amount || 0);
  const getValuePrice = (val) => Number(val?.price || val?.amount || 0);
  const getVariantDeltaForValue = (product, optKey, valKey, valueLabel) => {
    // Show only the delta of this value, not the whole variant combo
    return getValuePriceFromOptions(product, optKey, valueLabel);
  };

  const buildSelections = (item, selectionMap) => {
    const parseAvailable = (val) => {
      const n = Number(
        val?.qty ??
        val?.quantity ??
        val?.available ??
        val?.availableQty ??
        val?.stock
      );
      return Number.isFinite(n) ? n : null;
    };

    if (!item?.options || !Array.isArray(item.options)) return [];
    return item.options.map((opt, optIdx) => {
      const optKey = getOptionKey(opt, optIdx);
      const values = Array.isArray(opt.values) ? opt.values : Array.isArray(opt.options) ? opt.options : null;
      if (!values) return null;
      const enrichedValues = values.map((v, vIdx) => {
        const vKey = getValueKey(v, vIdx);
        const avail = parseAvailable(v);
        return { raw: v, key: vKey, available: avail };
      });
      const selectedValKey = selectionMap[optKey];
      const selectedVal =
        enrichedValues.find((v) => v.key === selectedValKey) ||
        enrichedValues.find((v) => v.available === null || v.available > 0) ||
        enrichedValues[0];
      const available = parseAvailable(selectedVal);
      return {
        optKey,
        optionLabel: opt.name || opt.label || opt.value || `Option ${optIdx + 1}`,
        valueKey: selectedVal ? selectedVal.key : null,
        valueLabel: selectedVal ? getValueLabel(selectedVal.raw, 0) : "",
        price: selectedVal ? getValuePrice(selectedVal.raw) : 0,
        available: parseAvailable(selectedVal.raw),
        values: enrichedValues.map((v) => ({
          key: v.key,
          label: getValueLabel(v.raw, 0),
          price: getValuePrice(v.raw),
          available: v.available,
        })),
      };
    }).filter(Boolean);
  };

  const findVariantMatch = (product, selections) => {
    if (!product?.variants?.length || !Array.isArray(selections) || selections.length === 0) return null;
    const norm = (v) => (v ?? "").toString().trim().toLowerCase();

    const selMap = new Map(
      selections.map((s) => [
        norm(s.optionLabel),
        norm(s.valueLabel),
      ])
    );

    const selectionValues = selections
      .map((s) => [norm(s.valueLabel), norm(s.valueKey || s.valueLabel)])
      .flat()
      .filter(Boolean)
      .sort();

    const variant = product.variants.find((v) => {
      const opts = v.options || [];
      const groupMatch = opts.every((o) => {
        const group = norm(o.group);
        const val = norm(o.value);
        return selMap.get(group) === val;
      });

      if (groupMatch) return true;

      const variantValues = [
        norm(v.name),
        norm(v.label),
        ...opts.map((o) => norm(o.value)),
      ].filter(Boolean);

      // Match if every selected value exists in variant values (order-insensitive)
      return selectionValues.every((val) => variantValues.includes(val));
    });
    if (!variant) return null;
    return {
      variant,
      name: (variant.options || []).map((o) => `${o.group}: ${o.value}`).join(" / "),
    };
  };

  const getSelectedUnitPrice = (item, selectionMap) => {
    const product = productsMap[item?.id] || item;
    const selections =
      item?.selectedOptions ||
      (selectionMap !== null ? buildSelections(item, selectionMap) : buildSelections(item, selectedProductOptionValues));

    const base = Number(item?.price || 0);
    const optionSum = Array.isArray(selections)
      ? selections.reduce((s, sel) => s + Number(sel?.price || 0), 0)
      : 0;

    return base + optionSum;
  };

  const getValuePriceFromOptions = (product, groupName, valueLabel) => {
    if (!product?.options) return 0;
    const norm = (v) => (v || '').toString().trim().toLowerCase();
    const group = product.options.find((g) => norm(g.name) === norm(groupName));
    if (!group || !Array.isArray(group.values)) return 0;
    const match = group.values.find(
      (v) => norm(v.label ?? v.value ?? v) === norm(valueLabel)
    );
    return Number(match?.price || 0);
  };

  const computeVariantValueDelta = (product, variant) => {
    if (!product || !variant) return 0;
    return (variant.options || []).reduce(
      (sum, opt) => sum + getValuePriceFromOptions(product, opt.group, opt.value),
      0
    );
  };

  const findVariantByOptionLabels = (product, selectionLabels) => {
    const norm = (v) => (v || "").toString().trim().toLowerCase();
    if (!product?.variants?.length) return null;
    return product.variants.find((v) => {
      const opts = v.options || [];
      return Object.entries(selectionLabels || {}).every(([group, val]) => {
        const found = opts.find((o) => norm(o.group) === norm(group));
        return found && norm(found.value) === norm(val);
      });
    });
  };

  const cleanVariantLabel = (label) => {
    const norm = (label || "").toString().trim().toLowerCase();
    if (!label) return "";
    if (norm.includes("any variant")) return "";
    return label;
  };

  const getVariantAvailable = (item, selectionMapOverride = null) => {
    const product = productsMap[item?.id] || item;
    const selections =
      item?.selectedOptions ||
      (selectionMapOverride !== null
        ? buildSelections(item, selectionMapOverride)
        : buildSelections(item, selectedProductOptionValues));
    if (product?.variants?.length) {
      if (!selections || selections.length === 0 || (selectionMapOverride && Object.keys(selectionMapOverride).length === 0)) {
        return product.variants.reduce((mx, v) => Math.max(mx, Number(v.qty || 0)), 0);
      }
      const match = findVariantMatch(product, selections);
      return match ? Number(match.variant?.qty ?? 0) : 0;
    }

    if (item?.variantId && product?.variants?.length) {
      const v = product.variants.find(
        (vr) => vr.id === item.variantId || vr.variantId === item.variantId
      );
      if (v) return Number(v.qty || 0);
    }

    if (!Array.isArray(selections)) return null;
    const avail = selections
      .map((s) => Number.isFinite(s.available) ? Number(s.available) : null)
      .filter((n) => n !== null);
    if (avail.length === 0) return null;
    return Math.min(...avail);
  };

  const buildBundleConfig = (bundle, selections = {}) => {
    let deltaTotal = 0;
    const resolvedItems = [];

    (bundle.items || []).forEach((sub) => {
      const product = productsMap[sub.id];
      const choices = Array.isArray(selections[sub.id]) ? selections[sub.id] : selections[sub.id] ? [selections[sub.id]] : [];
      if (sub.variantId !== ANY_VARIANT_ID && !sub.allowVariantChoice) {
        const fixedVariant = product?.variants?.find((v) => (v.id || v.variantId) === sub.variantId);
        const fixedDelta = fixedVariant ? computeVariantValueDelta(product, fixedVariant) : 0;
        resolvedItems.push({
          ...sub,
          variantPriceDelta: fixedDelta,
          selectedOptions: fixedVariant?.options || sub.selectedOptions || sub.options || [],
        });
        return;
      }
      if (!choices.length || !product?.variants?.length) {
        resolvedItems.push({ ...sub, variantId: null, variantName: '', variantPriceDelta: 0, selectedOptions: [] });
        return;
      }
      // aggregate chosen variants
      const counts = {};
      choices.forEach((vid) => {
        if (!vid) return;
        counts[vid] = (counts[vid] || 0) + 1;
      });
      Object.entries(counts).forEach(([vid, qty]) => {
        const variant = product.variants.find((v) => (v.id || v.variantId) === vid);
        const variantName = getVariantLabel(variant);
        const varDelta = computeVariantValueDelta(product, variant);
        deltaTotal += varDelta * qty;
        resolvedItems.push({
          ...sub,
          variantId: vid,
          variantName,
          quantity: qty,
          variantPriceDelta: varDelta,
          selectedOptions: variant?.options || sub.selectedOptions || sub.options || [],
        });
      });
    });

    const configKey = resolvedItems
      .map((it) => `${it.id}:${it.variantId || (it.variantId === ANY_VARIANT_ID ? "any" : "base")}`)
      .join("|");

    return { resolvedItems, deltaTotal, configKey: configKey || "default" };
  };

  const isValueSelectable = (product, optKey, valKey, valLabel, selectionLabels) => {
    const norm = (s) => (s || "").toString().trim().toLowerCase();
    const optNorm = norm(optKey);
    if (product?.variants?.length) {
      return product.variants.some((v) => {
        const qty = Number(v.qty || 0);
        if (qty <= 0) return false;
        const opts = v.options || [];
        const current = opts.find((o) => norm(o.group) === optNorm);
        if (!current) return false;
        const valNorm = norm(current.value);
        if (!(valNorm === norm(valKey) || valNorm === norm(valLabel))) return false;
        // other selections must also match
        for (const [selKey, selVal] of Object.entries(selectionLabels || {})) {
          if (selKey === optKey) continue;
          const other = opts.find((o) => norm(o.group) === norm(selKey));
          if (!other) return false;
          const otherValNorm = norm(other.value);
          if (!(otherValNorm === norm(selVal.key) || otherValNorm === norm(selVal.label))) return false;
        }
        return true;
      });
    }
    return true;
  };

  const canAddQuantity = (item, qtyToAdd, existingQty = 0) => {
    if (!item) return false;
    const variantAvail = getVariantAvailable(item);
    if (variantAvail !== null) {
      const reserved = getReservedCountForProduct(item.id, cart, item.variantId || null);
      const reservedOthers = reserved - existingQty;
      const effective = variantAvail - reservedOthers;
      return existingQty + qtyToAdd <= effective;
    }
    if (item.type === 'bundle') {
      const availableBundles = getAvailableBundleCount(item, productsMap, cart);
      return existingQty + qtyToAdd <= (availableBundles + existingQty);
    }
    const prod = productsMap[item.id] || item;
    const available = getAvailableProductCount(item.id, prod?.qty ?? item.qty ?? 0, cart);
    return existingQty + qtyToAdd <= (available + existingQty);
  };

  const addSelectedProductToCart = () => {
    if (!selectedProduct) return;
    const totalOptionGroups = Array.isArray(selectedProduct.options) ? selectedProduct.options.length : 0;
    if (totalOptionGroups && Object.keys(selectedProductOptionValues).length < totalOptionGroups) {
      toast.error("Please select all options before adding to basket.");
      return;
    }
    const qty = Math.max(1, selectedProductQuantity || 1);

    // Bundle path
    if (selectedProduct.type === "bundle") {
      const needsVariantChoice = (selectedProduct.items || []).some(
        (sub) => sub.variantId === ANY_VARIANT_ID || sub.allowVariantChoice
      );

      if (needsVariantChoice) {
        const missing = (selectedProduct.items || []).some(
          (sub) => (sub.variantId === ANY_VARIANT_ID || sub.allowVariantChoice) && !bundleDraftSelections[sub.id]
        );
        if (missing) {
          toast.error("Please select variants for all bundle items.");
          return;
        }
        addBundleWithConfig(selectedProduct, bundleDraftSelections, qty);
        closeProductModal();
        return;
      }

      // Fixed variants: add directly
      addBundleWithConfig(selectedProduct, {}, qty);
      closeProductModal();
      return;
    }

    const selections = buildSelections(selectedProduct, selectedProductOptionValues);
    const productRef = productsMap[selectedProduct.id] || selectedProduct;
    const variantMatch = findVariantMatch(productRef, selections);
    const variantId = variantMatch?.variant?.id || variantMatch?.variant?.variantId || null;
    const chosenVariant =
      variantMatch?.variant ||
      (variantId
        ? (productRef.variants || []).find(
            (v) => v.id === variantId || v.variantId === variantId
          )
        : null);
    const variantDelta = chosenVariant ? computeVariantValueDelta(productRef, chosenVariant) : 0;
    const selectionsWithAvail = selections.map((s) => ({
      ...s,
      available: variantMatch?.variant?.qty ?? s.available,
    }));
    const optionsKey = variantId
      ? `var:${variantId}`
      : selections.map((o) => `${o.optKey}:${o.valueKey}`).join('|');
    const unitPrice = getSelectedUnitPrice(selectedProduct, selectedProductOptionValues);

    const existingIdx = cart.findIndex(
      (ci) => ci.id === selectedProduct.id && (ci.optionsKey || "") === optionsKey
    );
    const existing = existingIdx >= 0 ? cart[existingIdx] : null;
    const existingQty = existing ? Number(existing.quantity || 0) : 0;

    if (!canAddQuantity({ ...selectedProduct, variantId, selectedOptions: selections }, qty, existingQty)) {
      toast.error(`${selectedProduct.name} is out of stock!`, { position: "top-center", autoClose: 1500 });
      return;
    }

    if (existing) {
      const updated = {
        ...existing,
        quantity: existingQty + qty,
        price: unitPrice,
        selectedOptions: selectionsWithAvail,
        variantId,
        variantName: variantMatch?.name || existing.variantName || null,
        variantPriceDelta: variantDelta,
      };
      const clone = [...cart];
      clone[existingIdx] = updated;
      setCart(clone);
    } else {
      setCart((prev) => [
        ...prev,
        {
          ...selectedProduct,
          quantity: qty,
          price: unitPrice,
          selectedOptions: selectionsWithAvail,
          variantId,
          variantName: variantMatch?.name || null,
          optionsKey,
          variantPriceDelta: variantDelta,
        },
      ]);
    }

    toast.success(`${selectedProduct.name} added to cart!`, { position: "top-center", autoClose: 1000 });
    setSelectedProduct(null);
    setSelectedProductOptionValues({});
    setSelectedProductQuantity(1);
  };

  const openProductModal = (item) => {
    if (isItemOutOfStock(item)) return;
    setSelectedProduct(item);
    // Force user to choose options; start with none selected
    setSelectedProductOptionValues({});
    setSelectedProductQuantity(1);
    setProductImageIndex(0);

    if (item.type === 'bundle') {
      const draft = {};
      (item.items || []).forEach((sub) => {
        if (!(sub.variantId === ANY_VARIANT_ID || sub.allowVariantChoice)) return;
        const product = productsMap[sub.id];
        const defaultVariant =
          product?.variants?.find((v) => Number(v.qty || 0) > 0) || product?.variants?.[0];
        const choice = defaultVariant ? (defaultVariant.id || defaultVariant.variantId) : '';
        const count = Math.max(1, Number(sub.quantity || 1));
        draft[sub.id] = Array.from({ length: count }, () => choice);
      });
      setBundleDraftSelections(draft);
    } else {
      setBundleDraftSelections({});
    }
  };

  const closeProductModal = () => {
    setSelectedProduct(null);
    setSelectedProductOptionValues({});
    setSelectedProductQuantity(1);
    setProductImageIndex(0);
    setBundleDraftSelections({});
  };

  const handleConfirmBundleVariants = () => {
    const { bundle, selections, qty } = bundleVariantContext;
    if (!bundle) return;
    const missing = (bundle.items || []).some(
      (sub) => (sub.variantId === ANY_VARIANT_ID || sub.allowVariantChoice) && !selections[sub.id]
    );
    if (missing) {
      toast.error("Please select variants for all bundle items.");
      return;
    }
    addBundleWithConfig(bundle, selections, Math.max(1, qty || 1));
  };


  const addToCart = (item) => {
    const isBundle = item.type === 'bundle';

    if (isBundle) {
      const needsVariantChoice = (item.items || []).some(
        (sub) => sub.variantId === ANY_VARIANT_ID || sub.allowVariantChoice
      );
      if (needsVariantChoice) {
        const selections = {};
        (item.items || []).forEach((sub) => {
          if (!(sub.variantId === ANY_VARIANT_ID || sub.allowVariantChoice)) return;
          const product = productsMap[sub.id];
          const defaultVariant =
            product?.variants?.find((v) => Number(v.qty || 0) > 0) || product?.variants?.[0];
          selections[sub.id] = defaultVariant?.id || defaultVariant?.variantId || "";
        });
        setBundleVariantContext({ bundle: item, selections, qty: 1 });
        setBundleVariantModalOpen(true);
        return;
      }

      // Compute how many bundles currently available
      const availableBundles = getAvailableBundleCount(item, productsMap, cart);
      const cartItem = cart.find(ci => ci.id === item.id);
      const currentInCart = cartItem ? Number(cartItem.quantity || cartItem.qty || 0) : 0;

      if (currentInCart + 1 > (availableBundles + currentInCart)) {
        toast.error(`${item.name} is out of stock!`, { position: "top-center", autoClose: 1500 });
        return;
      }

      setCart(prev => {
        const found = prev.find(ci => ci.id === item.id);
        if (found) {
          return prev.map(ci => ci.id === item.id ? { ...ci, quantity: (ci.quantity || ci.qty || 0) + 1, bundleBasePrice: Number(item.price || 0), variantPriceDelta: 0 } : ci);
        }
        // push a shallow copy and set quantity
        return [...prev, { ...item, quantity: 1, bundleBasePrice: Number(item.price || 0), variantPriceDelta: 0, optionsKey: "default", bundleConfigKey: "default" }];
      });

      toast.success(`${item.name} added to cart!`, { position: "top-center", autoClose: 1000 });
      return;
    }

    // product case
    const prod = productsMap[item.id] || item;
    const available = getAvailableProductCount(item.id, prod?.qty ?? item.qty ?? 0, cart);
    const cartItem = cart.find(ci => ci.id === item.id);
    const currentInCart = cartItem ? Number(cartItem.quantity || cartItem.qty || 0) : 0;

    if (currentInCart + 1 > (available + currentInCart)) {
      toast.error(`${item.name} is out of stock!`, { position: "top-center", autoClose: 1500 });
      return;
    }


    setCart(prev => {
      const found = prev.find(ci => ci.id === item.id);
      if (found) {
        return prev.map(ci => ci.id === item.id ? { ...ci, quantity: (ci.quantity || ci.qty || 0) + 1 } : ci);
      }
      return [...prev, { ...item, quantity: 1 , type: item.type || 'product'}];
    });

    toast.success(`${item.name} added to cart!`, { position: "top-center", autoClose: 1000 });
  };

  const addBundleWithConfig = (bundle, selections, qtyToAdd = 1) => {
    const { resolvedItems, deltaTotal, configKey } = buildBundleConfig(bundle, selections);
    const candidate = { ...bundle, items: resolvedItems };
    const availableBundles = getAvailableBundleCount(candidate, productsMap, cart);
    const existing = cart.find(
      (ci) => ci.id === bundle.id && (ci.bundleConfigKey || "default") === (configKey || "default")
    );
    const currentQty = existing ? Number(existing.quantity || 0) : 0;
    if (currentQty + qtyToAdd > (availableBundles + currentQty)) {
      toast.error(`${bundle.name} is out of stock!`, { position: "top-center", autoClose: 1500 });
      return false;
    }
    const bundlePrice = Number(bundle.price || 0) + deltaTotal;
    setCart((prev) => {
      if (existing) {
        return prev.map((ci) =>
          ci.id === bundle.id && (ci.bundleConfigKey || "default") === (configKey || "default")
            ? {
                ...ci,
                quantity: currentQty + qtyToAdd,
                items: resolvedItems,
                price: bundlePrice,
                variantPriceDelta: deltaTotal,
                bundleBasePrice: Number(bundle.price || 0),
              }
            : ci
        );
      }
      return [
        ...prev,
        {
          ...bundle,
          items: resolvedItems,
          price: bundlePrice,
          bundleConfigKey: configKey || "default",
          variantPriceDelta: deltaTotal,
          bundleBasePrice: Number(bundle.price || 0),
          optionsKey: configKey || "default",
          quantity: qtyToAdd,
        },
      ];
    });
    toast.success(`${bundle.name} added to cart!`, { position: "top-center", autoClose: 1000 });
    setBundleVariantModalOpen(false);
    setBundleVariantContext({ bundle: null, selections: {} });
    return true;
  };

  const updateQuantity = (itemId, newQuantity, optionsKey = "") => {
    if (newQuantity < 1) {
        // If the quantity drops to zero, remove it from the cart
        removeFromCart(itemId, optionsKey);
        return;
    }

    const itemToUpdate = cart.find(i => i.id === itemId && (i.optionsKey || "") === (optionsKey || ""));
    if (!itemToUpdate) return;
    const currentQuantity = Number(itemToUpdate.quantity || 0);
    
    // Only perform the stock check if the quantity is increasing
    if (newQuantity > currentQuantity) {
        let available = 0;
        const variantAvail = getVariantAvailable(itemToUpdate);
        
        // 💡 Calculate stock based on the full current inventory
        // (The getAvailable functions already correctly calculate this based on the current cart)
        if (variantAvail !== null) {
            const reserved = getReservedCountForProduct(itemId, cart, itemToUpdate.variantId || null);
            const reservedOthers = reserved - currentQuantity;
            available = variantAvail - reservedOthers;
        } else if (itemToUpdate.type === 'bundle') {
            available = getAvailableBundleCount(itemToUpdate, productsMap, cart) + currentQuantity;
        } else {
            const prod = productsMap[itemId] || itemToUpdate;
            available = getAvailableProductCount(itemId, prod?.qty ?? itemToUpdate.qty ?? 0, cart) + currentQuantity;
        }

        if (newQuantity > available) {
            toast.error(`${itemToUpdate.name} is out of stock! Max available: ${available}`, { position: "top-center", autoClose: 1500 });
            return; 
        }
    }

    // If stock check passes or quantity is decreasing, update the cart
    setCart((prev) =>
     prev.map((i) => (i.id === itemId && (i.optionsKey || "") === (optionsKey || "") ? { ...i, quantity: newQuantity } : i))
     );
  };

  const removeFromCart = (itemId, optionsKey = "") => {
    setCart((prev) => prev.filter((i) => !(i.id === itemId && (i.optionsKey || "") === (optionsKey || ""))));
  };

  const calculateCartTotals = () => {
    const subtotal = cart.reduce(
      (s, i) => s + Number(i.price || 0) * (i.quantity || 0),
      0
    );
    
    // Calculate taxes
    let sst6Amount = 0;
    let sst8Amount = 0;
    let serviceTax10Amount = 0;
    
    if (taxSettings.sst6Enabled) {
      sst6Amount = subtotal * 0.06;
    }
    
    if (taxSettings.sst8Enabled) {
      sst8Amount = subtotal * 0.08;
    }
    
    if (taxSettings.serviceTax10Enabled) {
      const rate = Number(taxSettings.serviceTax10Rate ?? 10);
      serviceTax10Amount = subtotal * (rate / 100);
    }
    
    const taxAmount = sst6Amount + sst8Amount + serviceTax10Amount;
    const discountBase = subtotal + taxAmount;
    const rawDiscount = selectedDiscount
      ? selectedDiscount.value.toString().includes('%')
        ? discountBase * (parseFloat(selectedDiscount.value) / 100)
        : parseFloat(selectedDiscount.value)
      : 0;
    const discountAmount = Math.max(0, Math.min(rawDiscount, discountBase));
    const total = Math.max(0, subtotal + taxAmount - discountAmount);
    
    return { 
      subtotal, 
      total,
      sst6Amount,
      sst8Amount,
      serviceTax10Amount,
      discountAmount
    };
  };

  const { subtotal, total, sst6Amount, sst8Amount, serviceTax10Amount, discountAmount } = calculateCartTotals();
  const stripeAllowedForTotal = Number(total || 0) >= 2;
  const paymentOptions = [
    ...(payAtCounterEnabled && duitnowQRUrl ? [{ value: "qr", label: "Pay with QR" }] : []),
    ...(stripePaymentEnabled && stripeAllowedForTotal
      ? [{ value: "stripe", label: "Pay Online (Card / FPX / Apple Pay / Google Pay)" }]
      : []),
  ];

  const formatPrice = (price) =>
    new Intl.NumberFormat("ms-MY", {
      style: "currency",
      currency: "MYR",
    }).format(price);

  const resetPaymentProof = () => {
    setPaymentProofFile(null);
    setPaymentProofError("");
  };

  const handlePaymentProofFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) {
      resetPaymentProof();
      return;
    }
    const allowedTypes = ["application/pdf"];
    const isImage = file.type.startsWith("image/");
    if (!isImage && !allowedTypes.includes(file.type)) {
      setPaymentProofFile(null);
      setPaymentProofError("Please upload an image or PDF file.");
      return;
    }
    setPaymentProofFile(file);
    setPaymentProofError("");
  };

  const uploadPaymentProof = async (orderId, file) => {
    if (!file) return null;
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
    const path = `companies/${companyId}/paymentProofs/${orderId}/${Date.now()}-${safeName}`;
    const proofRef = storageRef(storage, path);
    await uploadBytes(proofRef, file, { contentType: file.type || undefined });
    const url = await getDownloadURL(proofRef);
    return {
      paymentProofUrl: url,
      paymentProofName: file.name,
      paymentProofContentType: file.type || "",
      paymentProofPath: path,
      paymentProofUploadedAtIso: new Date().toISOString(),
    };
  };

  const handleOpenQr = () => {
    if (!duitnowQRUrl) return;
    window.open(duitnowQRUrl, "_blank", "noopener,noreferrer");
  };

  const renderQrPaymentBlock = () => {
    if (!duitnowQRUrl) {
      return (
        <div className="mt-4 rounded-lg border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-800">
          QR payment is not configured for this store yet.
        </div>
      );
    }

    return (
      <div className="mt-4 rounded-xl border border-purple-200 bg-purple-50 p-4">
        <p className="text-sm font-semibold text-purple-900">Pay with DuitNow QR</p>
        <p className="mt-1 text-xs text-purple-700">
          Scan the QR, complete payment, then upload your receipt screenshot or PDF proof.
        </p>
        <div className="mt-3 flex flex-col items-center gap-3">
          <img
            src={duitnowQRUrl}
            alt="DuitNow QR"
            className="max-h-64 w-full max-w-xs rounded-lg border border-purple-200 bg-white object-contain p-2"
          />
          <button
            type="button"
            onClick={handleOpenQr}
            className="rounded-lg bg-white px-4 py-2 text-sm font-medium text-purple-700 ring-1 ring-purple-200 hover:bg-purple-100"
          >
            Open QR in New Tab
          </button>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Upload Payment Proof <span className="text-red-500">*</span>
          </label>
          <input
            type="file"
            accept="image/*,application/pdf"
            onChange={handlePaymentProofFileChange}
            className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
          />
          {paymentProofFile && (
            <p className="mt-2 text-xs text-gray-600">Selected: {paymentProofFile.name}</p>
          )}
          {paymentProofError && (
            <p className="mt-2 text-xs text-red-600">{paymentProofError}</p>
          )}
        </div>
      </div>
    );
  };

  const requiresOrderType = stallType === 'fnb' && customerInfo.fulfillmentType !== "delivery";
  const qrProofMissing = selectedPaymentMethod === "qr" && !paymentProofFile;
  const placeOrderDisabled =
    isSubmitting ||
    cart.length === 0 ||
    (requiresOrderType && !customerInfo.orderType) ||
    qrProofMissing;

  const isDiscountWithinValidity = (discount) => {
    if (!discount) return true;
    const normalizeDate = (value, endOfDay = false) => {
      if (!value) return null;
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return null;
      if (endOfDay) {
        d.setHours(23, 59, 59, 999);
      } else {
        d.setHours(0, 0, 0, 0);
      }
      return d;
    };
    const now = new Date();
    const start = normalizeDate(discount.validityStart, false);
    const end = normalizeDate(discount.validityEnd, true);
    if (start && now < start) return false;
    if (end && now > end) return false;
    return true;
  };

  const generateOrderNumber = () => {
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.floor(1000 + Math.random() * 9000);
    return `ORD-${timestamp}${random}`;
  };

 const getAndUpdateQueueNumber = async () => {
    try {
      const queueRef = doc(db, "companies", companyId, "companySettings", "queueCounter");
      const today = new Date().toISOString().split("T")[0];

      // Use a Firestore transaction for safe concurrent updates
      const newQueueNumber = await runTransaction(db, async (transaction) => {
        const queueSnap = await transaction.get(queueRef);

        if (!queueSnap.exists()) {
          // Create new queue document
          transaction.set(queueRef, { 
            current: 1, 
            initialDate: today,
            lastUpdated: new Date(),
            updatedAt: serverTimestamp()
          });
          return 1;
        }

        const queueData = queueSnap.data();
        // Check lastUpdated field and compare dates
        const lastUpdatedDate = queueData?.lastUpdated?.toDate?.().toISOString().split('T')[0];
        let nextNumber = 1;

        if (lastUpdatedDate === today) {
          // Same day: increment
          nextNumber = (queueData.current || 0) + 1;
        } else {
          // New day: reset to 1
          nextNumber = 1;
        }

        transaction.update(queueRef, {
          current: nextNumber,
          initialDate: today,
          lastUpdated: new Date(),
          updatedAt: serverTimestamp()
        });

        return nextNumber;
      });

      return newQueueNumber;
    } catch (error) {
      console.error("Error getting/updating queue number:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "phone") {
      // Phone validation logic
      let phoneValue = value;
      
      // Ensure it starts with +60
      if (!phoneValue.startsWith("+60")) {
        phoneValue = "+60";
      }
      
      // Extract the part after +60
      const numberPart = phoneValue.substring(3);
      
      // Remove any non-digit characters
      const cleanNumber = numberPart.replace(/\D/g, "");
      
      // Prevent entering 0 as first digit - just don't allow it
      if (cleanNumber.length > 0 && cleanNumber[0] === "0") {
        return; // Don't update if starts with 0
      }
      
      if (cleanNumber.length > 10) {
        return; // Don't update if exceeds limit
      }
      
      if (cleanNumber.length > 0 && cleanNumber.length >= 9 && cleanNumber.length <= 10) {
        setPhoneError("");
      } else if (cleanNumber.length > 0) {
        setPhoneError("Invalid phone number");
      } else {
        setPhoneError("");
      }
      
      setCustomerInfo((prev) => ({ ...prev, [name]: "+60" + cleanNumber }));
    } else {
      if (name === "pickupDate") setPickupDateError("");
      if (name === "pickupTime") setPickupTimeError("");
      setCustomerInfo((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleStartOrder = async () => {
    if (!onlineOrderingLive) {
      toast.error(offlineReason || "Online ordering is currently closed.");
      return;
    }

    let isValid = true;
    const todayStr = getTodayStr();
    const maxDateStr = getDateStrDaysFromToday(5);
    const nowTimeStr = getNowTimeStr();
    const nowMinutes = parseTimeToMinutes(nowTimeStr);
    const selectedSchedule = getScheduleForDate(customerInfo.pickupDate || todayStr);

    if (!customerInfo.name.trim()) {
      setNameError("Please enter your name.");
      isValid = false;
    } else {
      setNameError("");
    }

    if (!customerInfo.phone.trim() || customerInfo.phone === "+60") {
      setPhoneError("Please enter your phone number.");
      isValid = false;
    } else {
      setPhoneError("");
    }

    if (customerInfo.fulfillmentType === "pickup" && !pickupEnabled) {
      toast.error("Pickup is not available right now.");
      return;
    }

    if (customerInfo.fulfillmentType === "delivery") {
      if (!deliveryEnabled) {
        toast.error("Delivery is not available right now.");
        return;
      }
      if (!customerInfo.deliveryAddress.trim()) {
        toast.error("Please enter a delivery address.");
        isValid = false;
      }
    }

    const isScheduledPickup = customerInfo.pickupOption === "later";

    if (isScheduledPickup) {
      if (!customerInfo.pickupDate) {
        setPickupDateError("Please select a pickup date.");
        isValid = false;
      } else {
        setPickupDateError("");
      }

      if (customerInfo.pickupDate && (customerInfo.pickupDate < todayStr || customerInfo.pickupDate > maxDateStr)) {
        setPickupDateError(`Pickup date must be within the next 5 days.`);
        isValid = false;
      }

      if (customerInfo.pickupDate && !selectedSchedule.enabled) {
        setPickupDateError("Store is closed on the selected date.");
        isValid = false;
      }

      if (!customerInfo.pickupTime) {
        setPickupTimeError("Please select a pickup time.");
        isValid = false;
      } else {
        setPickupTimeError("");
      }

      if (customerInfo.pickupDate && customerInfo.pickupTime) {
        const selectedDate = customerInfo.pickupDate;
        const selectedTime = customerInfo.pickupTime;
        if (selectedDate === todayStr) {
          const selectedMinutes = parseTimeToMinutes(selectedTime);
          if (selectedMinutes !== null && nowMinutes !== null && selectedMinutes < nowMinutes) {
            setPickupTimeError("Please pick a future time for today.");
            isValid = false;
          }
        }

        const selectedMinutes = parseTimeToMinutes(selectedTime);
        if (selectedMinutes !== null && !isTimeWithinStoreHours(selectedTime, selectedSchedule)) {
          const openMins = parseTimeToMinutes(selectedSchedule?.open);
          const closeMins = parseTimeToMinutes(selectedSchedule?.close);
          const openLabel = minutesToTimeString(openMins !== null ? openMins + 30 : null);
          const closeLabel = minutesToTimeString(closeMins !== null ? closeMins - 30 : null);
          setPickupTimeError(
            selectedSchedule?.open && selectedSchedule?.close
              ? `Please pick a time between ${openLabel || selectedSchedule.open} and ${closeLabel || selectedSchedule.close}.`
              : "Please pick a time during store hours."
          );
          isValid = false;
        }
      }
    } else {
      setPickupDateError("");
      setPickupTimeError("");
    }

    if (!isValid) return;
    
    if (!privacyAccepted) {
      setPrivacyError(true);
      return;
    }
    setPrivacyError(false);
    
    // Check if phone number already has existing orders
    try {
      const phoneNumber = customerInfo.phone.replace(/\s+/g, '');
      const phoneOrdersRef = collection(db, "companies", companyId, "onlineOrders", phoneNumber, "data");
      const existingOrdersSnap = await getDocs(phoneOrdersRef);
      
      const existingOrders = [];
      existingOrdersSnap.forEach((doc) => {
        const data = doc.data();
        existingOrders.push(data);
      });
      
      if (existingOrders.length > 0) {
        const queueNumbers = existingOrders.map(o => o.queueNumber).join(", ");
        setConfirmModalData({
          message: `There's an existing order under ${customerInfo.phone} with queue number(s) #${queueNumbers}. Would you like to continue with a new order? You're previous order will NOT be deleted.`,
          onConfirm: () => {
            setShowConfirmModal(false);
            setCurrentPage(2);
          },
          onCancel: () => {
            setShowConfirmModal(false);
          }
        });
        setShowConfirmModal(true);
        return;
      }
    } catch (error) {
      console.error("Error checking existing orders:", error);
    }
    
    setCurrentPage(2);
  };

  const handleItemNoteChange = (itemId, note) => {
    setItemNote((prev) => ({ ...prev, [itemId]: note }));
  };

  const startStripeCheckout = async (orderId) => {
    if (!orderId || !companyId) return;
    setIsPaying(true);
    try {
      const createCheckout = httpsCallable(functions, "createOnlineOrderCheckoutSession");
      const { data } = await createCheckout({
        companyId,
        orderId,
        origin: window.location.origin,
      });
      if (!data?.url) {
        throw new Error("Missing checkout URL");
      }
      window.location.href = data.url;
    } catch (err) {
      console.error("Failed to start checkout:", err);
      toast.error("Unable to start payment. Please try again or switch to Pay with QR.");
      setIsPaying(false);
    }
  };

  const handleSubmitQrProofForPlacedOrder = async () => {
    if (!placedOrder?.orderId || !companyId) return;
    if (!paymentProofFile) {
      setPaymentProofError("Please upload your payment proof first.");
      return;
    }
    if (!duitnowQRUrl) {
      toast.error("QR payment is not configured.");
      return;
    }
    setIsUploadingPaymentProof(true);
    try {
      const proofPayload = await uploadPaymentProof(placedOrder.orderId, paymentProofFile);
      const orderRef = doc(db, "companies", companyId, "preorders", placedOrder.orderId);
      await updateDoc(orderRef, {
        paymentMethod: "DuitNow QR",
        paymentStatus: "pending_verification",
        paymentProvider: "duitnow_qr",
        ...proofPayload,
      });
      setPlacedOrder((prev) => ({
        ...prev,
        paymentMethod: "DuitNow QR",
        paymentStatus: "pending_verification",
        paymentProvider: "duitnow_qr",
        ...proofPayload,
      }));
      setSelectedPaymentMethod("qr");
      resetPaymentProof();
      toast.success("Payment proof uploaded. Waiting for verification.");
    } catch (err) {
      console.error("Failed to upload payment proof:", err);
      toast.error("Unable to upload payment proof. Please try again.");
    } finally {
      setIsUploadingPaymentProof(false);
    }
  };

const handleSubmitOrder = async () => {
  if (!onlineOrderingLive) {
    toast.error(offlineReason || "Online ordering is currently closed.");
    return;
  }

  if (cart.length === 0) {
    toast.error("Your cart is empty");
    return;
  }
  if (!payAtCounterEnabled && !stripePaymentEnabled) {
    toast.error("No payment methods available. Please contact the merchant.");
    return;
  }
  if (selectedPaymentMethod === "qr" && (!payAtCounterEnabled || !duitnowQRUrl)) {
    toast.error("QR payment is not available right now.");
    return;
  }
  if (selectedPaymentMethod === "stripe" && !stripePaymentEnabled) {
    toast.error("Online payment is not available right now.");
    return;
  }
  if (selectedPaymentMethod === "stripe" && !stripeAllowedForTotal) {
    toast.error("Orders below RM2 cannot use online payment. Please use Pay with QR.");
    return;
  }
  if (selectedPaymentMethod === "stripe" && Number(total || 0) <= 0) {
    toast.error("Online payment requires a positive total.");
    return;
  }
  if (selectedPaymentMethod === "qr" && !paymentProofFile) {
    setPaymentProofError("Please upload your payment proof before placing the order.");
    toast.error("Payment proof is required for QR payments.");
    return;
  }
  if (customerInfo.fulfillmentType === "delivery" && !customerInfo.deliveryAddress.trim()) {
    toast.error("Please enter a delivery address.");
    return;
  }

  // Only require order type selection for F&B stall type
  if (stallType === 'fnb' && customerInfo.fulfillmentType !== "delivery" && !customerInfo.orderType) {
    toast.error("Please select order type (Dine In or Take Away)");
    return;
  }

  const buildStockNeeds = () => {
    const productNeeds = {};
    const variantNeeds = {};

    cart.forEach((item) => {
      const qty = Number(item.quantity || 0);
      if (qty <= 0) return;

      const variantAvail = getVariantAvailable(item);
      if (variantAvail !== null) {
        const key = `${item.id}::${item.variantId || item.optionsKey || "variant"}`;
        variantNeeds[key] = (variantNeeds[key] || 0) + qty;
        productNeeds[item.id] = (productNeeds[item.id] || 0) + qty;
        return;
      }

      if (item.type === "bundle" && Array.isArray(item.items)) {
        item.items.forEach((sub) => {
          const need = Number(sub.quantity || 1) * qty;
          if (!sub.id) return;
          if (sub.variantId) {
            const key = `${sub.id}::${sub.variantId}`;
            variantNeeds[key] = (variantNeeds[key] || 0) + need;
            productNeeds[sub.id] = (productNeeds[sub.id] || 0) + need; // decrement master qty too
          } else {
            productNeeds[sub.id] = (productNeeds[sub.id] || 0) + need;
          }
        });
      } else {
        productNeeds[item.id] = (productNeeds[item.id] || 0) + qty;
      }
    });

    return { productNeeds, variantNeeds };
  };

  // Preflight stock validation to handle concurrent users
  const preflightStockCheck = async (needs) => {
    const { productNeeds, variantNeeds } = needs || buildStockNeeds();
    const productIds = Array.from(
      new Set([
        ...Object.keys(productNeeds),
        ...Object.keys(variantNeeds).map((k) => k.split("::")[0]),
      ])
    );
    const insufficient = [];
    const productDataCache = {};

    // Fetch all needed products once
    await Promise.all(
      productIds.map(async (pid) => {
        const prodRef = doc(db, "companies", companyId, "inventory", "data", "products", pid);
        const snap = await getDoc(prodRef);
        if (snap.exists()) {
          productDataCache[pid] = snap.data();
        } else {
          productDataCache[pid] = null;
        }
      })
    );

    // Variant-level checks against live variant qty
    Object.entries(variantNeeds).forEach(([key, needQty]) => {
      const [pid, variantId] = key.split("::");
      const pdata = productDataCache[pid];
      const variant = pdata?.variants?.find((v) => v.id === variantId || v.variantId === variantId);
      const available = variant ? Number(variant.qty || 0) : 0;
      if (available < needQty) {
        insufficient.push({
          id: key,
          needed: needQty,
          available,
          name: variant?.options?.map((o) => `${o.group}: ${o.value}`).join(" / ") || "Selected variant",
        });
      }
    });

    // Product-level checks
    if (insufficient.length === 0 && Object.keys(productNeeds).length > 0) {
      Object.entries(productNeeds).forEach(([pid, needQty]) => {
        const pdata = productDataCache[pid];
        const available = pdata ? Number(pdata.qty || 0) : 0;
        if (available < needQty) {
          insufficient.push({
            id: pid,
            needed: needQty,
            available,
            name: pdata?.name || pid,
          });
        }
      });
    }

    if (insufficient.length > 0) {
      const msg = insufficient
        .map((i) => `${i.name} (need ${i.needed}, available ${i.available})`)
        .join(", ");
      return { ok: false, message: `Item out of stock: ${msg}` };
    }

    return { ok: true };
  };

  const preflightNeeds = buildStockNeeds();
  const stockCheck = await preflightStockCheck(preflightNeeds);
  if (!stockCheck.ok) {
    toast.error(stockCheck.message || "Some items are out of stock.");
    return;
  }

  setIsSubmitting(true);

  try {
    const now = new Date();
    const todayStr = now.toISOString().split("T")[0];
    const currentTimeStr = now
      .toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

    const pickupOptionValue = ["later", "preorder"].includes(customerInfo.pickupOption)
      ? customerInfo.pickupOption
      : "now";

    const pickupMeta = pickupOptionValue === "now"
      ? {
          pickupOption: "now",
          pickupDate: todayStr,
          pickupTime: currentTimeStr,
        }
      : pickupOptionValue === "preorder"
      ? {
          pickupOption: "preorder",
          pickupDate: null,
          pickupTime: null,
        }
      : {
          pickupOption: pickupOptionValue,
          pickupDate: customerInfo.pickupDate,
          pickupTime: customerInfo.pickupTime,
        };

    const orderNum = generateOrderNumber();
    const queue = await getAndUpdateQueueNumber();
    const phoneNumber = customerInfo.phone.replace(/\s+/g, "");

    const paymentMethodLabel = selectedPaymentMethod === "stripe" ? "Stripe Checkout" : "DuitNow QR";
    const paymentProvider = selectedPaymentMethod === "stripe" ? "stripe" : "duitnow_qr";
    const proofPayload = selectedPaymentMethod === "qr"
      ? await uploadPaymentProof(orderNum, paymentProofFile)
      : null;

    const fullOrderData = {
      orderNumber: orderNum,
      queueNumber: queue,
      customerInfo,
      items: cart.map((item) => ({
        id: item.id,
        name: item.name,
        price: item.price,
        qty: item.quantity,
        quantity: item.quantity,
        note: itemNote[getNoteKey(item)] || "",
        type: item.type || "product",
        variantId: item.variantId || null,
        variantName: item.variantName || null,
        variantPriceDelta: Number(item.variantPriceDelta || 0),
        selectedOptions: item.selectedOptions || item.options || [],
        items:
          item.type === "bundle" && Array.isArray(item.items)
            ? item.items
            : null,
        createdAt: item.createdAt || new Date().toISOString(),
        updatedAt: item.updatedAt || new Date().toISOString(),
      })),
      totals: {
        subtotal,
        sst6Amount,
        sst8Amount,
        serviceTax10Amount,
        discountAmount,
        total,
      },
      paymentMethod: paymentMethodLabel,
      mode: mode,
      orderMode: "ONLINE",
      fromOnline: true,
      orderStatus: "pending",
      paymentStatus: selectedPaymentMethod === "qr" ? "pending_verification" : "unpaid",
      paymentProvider,
      discount: selectedDiscount?.name || '-',
      discountCode: appliedCode || '',
      customerName: customerInfo.name,
      customerPhone: customerInfo.phone,
      fulfillmentType: customerInfo.fulfillmentType || "pickup",
      deliveryAddress: customerInfo.fulfillmentType === "delivery" ? customerInfo.deliveryAddress : "",
      orderType: customerInfo.fulfillmentType === "delivery" ? "delivery" : (customerInfo.orderType || null),
      ...(proofPayload || {}),
      ...pickupMeta,
      timestamp: serverTimestamp(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const onlineRefData = {
      orderId: orderNum,
      customerPhone: phoneNumber,
      queueNumber: queue,
      createdAt: serverTimestamp(),
    };

    const preOrderRef = doc(db, "companies", companyId, "preorders", orderNum);
    const onlineOrderRef = doc(
      db,
      "companies",
      companyId,
      "onlineOrders",
      phoneNumber,
      "data",
      orderNum
    );

    const txNeeds = buildStockNeeds();

    // ✅ Transaction ensures atomic stock check + deduction + order save
    await runTransaction(db, async (transaction) => {
      const insufficientStockItems = [];
      const productDataCache = {};
      const productRefs = {};
      const pendingUpdatesByProduct = {};

      const productIds = Array.from(
        new Set([
          ...Object.keys(txNeeds.productNeeds || {}),
          ...Object.keys(txNeeds.variantNeeds || {}).map((k) => k.split("::")[0]),
        ])
      );

      // Read all product docs first
      for (const pid of productIds) {
        const ref = doc(
          db,
          "companies",
          companyId,
          "inventory",
          "data",
          "products",
          pid
        );
        productRefs[pid] = ref;
        const snap = await transaction.get(ref);
        productDataCache[pid] = snap.exists() ? snap.data() : null;
      }

      // Check variant-level needs
      Object.entries(txNeeds.variantNeeds || {}).forEach(([key, needQty]) => {
        const [pid, variantId] = key.split("::");
        const pdata = productDataCache[pid];
        const variant = pdata?.variants?.find(
          (v) => v.id === variantId || v.variantId === variantId
        );
        const available = variant ? Number(variant.qty || 0) : 0;
        if (!variant || available < needQty) {
          insufficientStockItems.push({
            name:
              variant?.options
                ?.map((o) => `${o.group}: ${o.value}`)
                .join(" / ") || "Selected variant",
            available,
            required: needQty,
          });
        } else {
          pendingUpdatesByProduct[pid] = pendingUpdatesByProduct[pid] || {
            qtyDeduct: 0,
            variantDeduct: {},
          };
          pendingUpdatesByProduct[pid].variantDeduct[variantId] =
            (pendingUpdatesByProduct[pid].variantDeduct[variantId] || 0) + needQty;
        }
      });

      // Check product-level needs
      Object.entries(txNeeds.productNeeds || {}).forEach(([pid, needQty]) => {
        const pdata = productDataCache[pid];
        const available = pdata ? Number(pdata.qty || 0) : 0;
        if (!pdata || available < needQty) {
          insufficientStockItems.push({
            name: pdata?.name || "Unnamed item",
            available,
            required: needQty,
          });
        } else {
          pendingUpdatesByProduct[pid] = pendingUpdatesByProduct[pid] || {
            qtyDeduct: 0,
            variantDeduct: {},
          };
          pendingUpdatesByProduct[pid].qtyDeduct += needQty;
        }
      });

      if (insufficientStockItems.length > 0) {
        const message = insufficientStockItems
          .map((i) => `${i.name} (available: ${i.available})`)
          .join(", ");
        throw new Error(`Insufficient stock for: ${message}`);
      }

      // Mark discount code as redeemed if applicable
      if (selectedDiscount?.id && (selectedDiscount.appliedCode || appliedCode)) {
        const usedCode = (selectedDiscount.appliedCode || appliedCode).toUpperCase();
        const discountRef = doc(db, 'companies', companyId, 'companySettings', 'discounts', 'data', selectedDiscount.id);
        const snap = await transaction.get(discountRef);
        if (snap.exists()) {
          const data = snap.data();
          const existingRedeemed = data.redeemedCodes || [];
          const codes = data.codes || (data.code ? [data.code] : []);
          const generated = data.generated;
          const voucherLimit = Number(data.voucherCount || codes.length || 0);
          if (generated) {
            if (!existingRedeemed.map((c) => c.toUpperCase()).includes(usedCode)) {
              transaction.update(discountRef, { redeemedCodes: [...existingRedeemed, usedCode] });
            }
          } else {
            if (!voucherLimit || existingRedeemed.length < voucherLimit) {
              transaction.update(discountRef, { redeemedCodes: [...existingRedeemed, usedCode] });
            }
          }
        }
      }

      // Apply stock deductions after all reads
      Object.entries(pendingUpdatesByProduct).forEach(([pid, update]) => {
        const payload = {};
        const pdata = productDataCache[pid] || {};

        if (update.qtyDeduct > 0) {
          const currentQty = Number(pdata.qty || 0);
          payload.qty = Math.max(0, currentQty - update.qtyDeduct);
        }

        if (update.variantDeduct && Object.keys(update.variantDeduct).length > 0) {
          const variants = Array.isArray(pdata.variants) ? pdata.variants : [];
          const updatedVariants = variants.map((v) => {
            const vid = v.id || v.variantId;
            const deduct = update.variantDeduct[vid];
            if (!deduct) return v;
            const currentQty = Number(v.qty || 0);
            return { ...v, qty: Math.max(0, currentQty - deduct) };
          });
          payload.variants = updatedVariants;
        }

        transaction.update(productRefs[pid], payload);
      });

      // ✅ Only save order if all stock checks pass
      transaction.set(preOrderRef, fullOrderData);
      transaction.set(onlineOrderRef, onlineRefData);
    });

      // ✅ Transaction completed successfully
      setPlacedOrder({
        ...fullOrderData,
        orderId: orderNum,
        phoneNumber,
        orderNumber: orderNum,
        queueNumber: queue,
      });
      if (selectedDiscount?.id && (selectedDiscount.appliedCode || appliedCode)) {
        const usedCode = (selectedDiscount.appliedCode || appliedCode).toUpperCase();
        setDiscountOptions((prev) =>
          prev.map((d) =>
            d.id === selectedDiscount.id
              ? { ...d, redeemedCodes: [...(d.redeemedCodes || []), usedCode] }
              : d
          )
        );
        setSelectedDiscount((prev) =>
          prev && prev.id === selectedDiscount.id
            ? { ...prev, redeemedCodes: [...(prev.redeemedCodes || []), usedCode] }
            : prev
        );
      }
      setDiscountCodeInput('');
      setDiscountError('');
      setSelectedDiscount(null);
      setAppliedCode('');

      setQueueNumber(queue);
      setCurrentPage(3);
      setShowMobileCheckout(false);
      toast.success("Order placed successfully!");

      // Cancel timer logic
      setShowCancelButton(true);
      const deadline = Date.now() + 15000;
      setCancelDeadline(deadline);
      sessionStorage.setItem(cancelDeadlineKey, String(deadline));
      setCancelCountdown(15);

      if (selectedPaymentMethod === "stripe") {
        await startStripeCheckout(orderNum);
      }
    } catch (error) {
    console.error("Error submitting order:", error);
    toast.error(error.message || "Failed to place order. Please try again.");
  } finally {
    setIsSubmitting(false);
  }
};


  const handleCancelOrder = async () => {
    const phoneNumber =
      placedOrder?.phoneNumber ||
      placedOrder?.customerPhone ||
      placedOrder?.customerInfo?.phone ||
      "";
    if (!placedOrder?.orderId || !phoneNumber) {
      toast.error("Missing order details for cancellation.");
      return;
    }
    setIsCancelling(true);

    try {
      const paymentStatus = (placedOrder.paymentStatus || "").toLowerCase();
      const paymentMethod = (placedOrder.paymentMethod || "").toLowerCase();
      const needsRefund = paymentStatus === "paid" && paymentMethod.includes("stripe");
      if (needsRefund) {
        const refundPayment = httpsCallable(functions, "refundOnlineOrderPayment");
        await refundPayment({ companyId, orderId: placedOrder.orderId });
      }

      const voidReason = "customer refund";
      const orderDocRef = doc(db, "companies", companyId, "orders", placedOrder.orderId);
      const orderDocSnap = await getDoc(orderDocRef);
      if (orderDocSnap.exists()) {
        await updateDoc(orderDocRef, {
          paymentStatus: "Void",
          voidReason,
          voidedAt: new Date().toISOString(),
        });
      }
      if (publicId) {
        const publicReceiptRef = doc(db, "publicUsers", publicId, "receipts", placedOrder.orderId);
        const publicSnap = await getDoc(publicReceiptRef);
        if (publicSnap.exists()) {
          await updateDoc(publicReceiptRef, {
            paymentStatus: "Void",
            voidReason,
            voidedAt: new Date().toISOString(),
          });
        }
      }

      // 🔥 Accumulate ALL products/variants to restore (mirror deduction logic)
      const productRestorations = {};
      const parseQty = (value, fallback = 0) => {
        const n = Number(value ?? fallback);
        return Number.isFinite(n) ? n : 0;
      };
      const addRestore = (productId, qtyToRestore, variantId = null) => {
        if (!productId || qtyToRestore <= 0) return;
        const entry = productRestorations[productId] || { qty: 0, variants: {} };
        entry.qty += qtyToRestore;
        if (variantId && variantId !== ANY_VARIANT_ID) {
          entry.variants[variantId] = (entry.variants[variantId] || 0) + qtyToRestore;
        }
        productRestorations[productId] = entry;
      };

      placedOrder.items?.forEach((item) => {
        const itemQty = parseQty(item.quantity ?? item.qty, 0);
        if (item.type === "bundle" && Array.isArray(item.items)) {
          item.items.forEach((sub) => {
            const subQty = parseQty(sub.quantity ?? sub.qty, 1);
            addRestore(sub.id, subQty * itemQty, sub.variantId || null);
          });
          return;
        }

        addRestore(item.id, itemQty, item.variantId || null);
      });

      // Restore inventory for all accumulated products
      const inventoryCollection = collection(db, "companies", companyId, "inventory", "data", "products");
      const restorePromises = Object.entries(productRestorations).map(([productId, info]) =>
        (async () => {
          const productRef = doc(inventoryCollection, productId);
          const productSnap = await getDoc(productRef);
          if (productSnap.exists()) {
            const data = productSnap.data() || {};
            const currentQty = Number(data.qty || 0);
            const updated = { qty: currentQty + (info.qty || 0) };

            if (info.variants && Object.keys(info.variants).length > 0) {
              const variants = Array.isArray(data.variants) ? data.variants : [];
              const nextVariants = variants.map((v) => {
                const vid = v.id || v.variantId;
                const restore = info.variants[vid];
                if (!restore) return v;
                const current = Number(v.qty || 0);
                return { ...v, qty: current + restore };
              });
              updated.variants = nextVariants;
            }

            await updateDoc(productRef, updated);
          }
        })()
      );

      await Promise.all(restorePromises);

      // 🗑️ Delete order
      await Promise.all([
        deleteDoc(doc(db, "companies", companyId, "preorders", placedOrder.orderId)),
        deleteDoc(doc(db, "companies", companyId, "onlineOrders", phoneNumber.replace(/\s+/g, ''), "data", placedOrder.orderId)),
      ]);

      // ✅ Now reset to page 1 first
      setCurrentPage(1);
      setCart([]);
      setItemNote({});
      setPlacedOrder(null);
      setShowCancelButton(true);
      setCancelDeadline(null);
      sessionStorage.removeItem(cancelDeadlineKey);
      setCustomerInfo({ name: "", phone: "", fulfillmentType: "pickup", pickupOption: "now", pickupDate: "", pickupTime: "", deliveryAddress: "", orderType: "" });
      setIsCancelling(false);

      // ✅ Show toast after reset
      setTimeout(() => {
        toast.success("Order cancelled successfully!", {
          position: "top-center",
          autoClose: 2000,
        });
      }, 300);

    } catch (error) {
      console.error("Error cancelling order:", error);
      toast.error("Failed to cancel order. Refund was not completed.");
      setIsCancelling(false);
    }
  };


  const fetchOrderHistory = () => {
    setShowPhonePrompt(true);
  };
  
const handlePhonePromptSubmit = async () => {
  const phone = phonePromptValue;
  if (!phone || phone === "+60" || phone.trim() === "+60") {
    toast.error("Phone number is required");
    return;
  }

  setIsSubmittingNumber(true);

  try {
    const phoneNumber = phone.replace(/\s+/g, ''); // Remove spaces
    const phoneOrdersRef = collection(db, "companies", companyId, "onlineOrders", phoneNumber, "data");
    const ordersSnap = await getDocs(phoneOrdersRef);

    // 🟣 Case 1: No document found for this phone number
    if (ordersSnap.empty) {
      setShowPhonePrompt(false);
      setCurrentPage(4); // redirect to "No Order Found" page
      return;
    }

    // 🟣 Fetch full order data from preOrders
    const userOrders = await Promise.all(
      ordersSnap.docs.map(async (docSnap) => {
        const refData = docSnap.data();
        const orderId = refData.orderId || docSnap.id;

        const preOrderRef = doc(db, "companies", companyId, "preorders", orderId);
        const preOrderSnap = await getDoc(preOrderRef);

        if (preOrderSnap.exists()) {
          const data = preOrderSnap.data();

          // If items missing or empty, treat as invalid order
          if (!data.items || data.items.length === 0) {
            return { id: orderId, missing: true };
          }

          return {
            id: orderId,
            phoneNumber,
            ...data,
          };
        } else {
          // Preorder deleted or missing
          return { id: orderId, missing: true };
        }
      })
    );

    // 🟣 Filter valid orders
    const validOrders = userOrders
      .filter((order) => !order.missing)
      .sort((a, b) => (b.queueNumber || 0) - (a.queueNumber || 0));

    // 🟣 Case 2: All orders missing or invalid
    if (validOrders.length === 0) {
      setShowPhonePrompt(false);
      setCurrentPage(4);
      return;
    }

    // 🟣 Case 3: Only one valid order
    if (validOrders.length === 1) {
      const orderData = validOrders[0];
      setPlacedOrder({
        ...orderData,
        orderId: orderData.id,
      });
      setCustomerInfo({
        name: orderData.customerName || orderData.customerInfo?.name || "",
        phone: orderData.customerPhone || orderData.customerInfo?.phone || "",
        pickupOption: orderData.pickupOption || orderData.customerInfo?.pickupOption || "now",
        pickupDate: orderData.pickupDate || orderData.customerInfo?.pickupDate || "",
        pickupTime: orderData.pickupTime || orderData.customerInfo?.pickupTime || "",
      });
      setShowCancelButton(false);
      setCancelDeadline(null);
      sessionStorage.removeItem(cancelDeadlineKey);
      setShowPhonePrompt(false);
      setPhonePromptValue("+60");
      setCurrentPage(3);
    } else {
      // 🟣 Case 4: Multiple valid orders → open selection modal
      setOrderHistoryList(validOrders);
      setShowOrderHistoryModal(true);
      setShowPhonePrompt(false);
      setPhonePromptValue("+60");
    }
  } catch (error) {
    console.error("Error fetching order history:", error);
    setCurrentPage(4); // fallback to "No Order" page if any fetch fails
  } finally {
    setIsSubmittingNumber(false);
  }
};

  
  const handleSelectOrder = (order) => {
    setPlacedOrder({
      ...order,
      orderId: order.id,
    });
    setCustomerInfo({
      name: order.customerName || order.customerInfo?.name || "",
      phone: order.customerPhone || order.customerInfo?.phone || "",
      pickupOption: order.pickupOption || order.customerInfo?.pickupOption || "now",
      pickupDate: order.pickupDate || order.customerInfo?.pickupDate || "",
      pickupTime: order.pickupTime || order.customerInfo?.pickupTime || "",
    });
    setShowCancelButton(false);
    setCancelDeadline(null);
    sessionStorage.removeItem(cancelDeadlineKey);
    setCurrentPage(3);
    setShowOrderHistoryModal(false);
    setOrderHistoryList([]);
  };

  const currentDateTime = currentTime.toLocaleString("en-MY", {
    dateStyle: "full",
    timeStyle: "short",
  });
  const selectedPickupDate = customerInfo.pickupDate || getTodayStr();
  const selectedSchedule = getScheduleForDate(selectedPickupDate);
  const { min: pickupTimeMin, max: pickupTimeMax } = getPickupTimeBounds(selectedPickupDate, selectedSchedule);

  // Listen to order status changes on Page 3
  useEffect(() => {
    if (currentPage === 3 && placedOrder?.orderId) {
      // Listen to the source of truth for status: preorders/{orderId}
      const orderDocRef = doc(db, "companies", companyId, "preorders", placedOrder.orderId);

      let active = true;
      const unsubscribe = onSnapshot(orderDocRef, (docSnap) => {
        if (!docSnap.exists()) {
          (async () => {
            try {
              const orderRef = doc(db, "companies", companyId, "orders", placedOrder.orderId);
              const orderSnap = await getDoc(orderRef);
              const paymentStatus = (orderSnap.data()?.paymentStatus || "").toLowerCase();
              if (!active) return;
              if (paymentStatus === "void" || paymentStatus === "refunded") {
                toast.info("Your order was voided. Please contact the merchant if needed.", {
                  position: "top-center",
                  autoClose: 2000,
                });
              } else {
                toast.info("Your order has been completed and paid!", {
                  position: "top-center",
                  autoClose: 2000,
                });
              }
            } catch (err) {
              if (!active) return;
              toast.info("Your order has been completed and paid!", {
                position: "top-center",
                autoClose: 2000,
              });
            }
            setTimeout(() => {
              setCurrentPage(1);
              setCart([]);
              setItemNote({});
              setPlacedOrder(null);
              setShowCancelButton(true);
              setCancelDeadline(null);
              sessionStorage.removeItem(cancelDeadlineKey);
              setCustomerInfo({ name: "", phone: "", fulfillmentType: "pickup", pickupOption: "now", pickupDate: "", pickupTime: "", deliveryAddress: "", orderType: "" });
            }, 2000);
          })();
          return;
        }
        const data = docSnap.data() || {};
        // Only update fields if they exist to avoid overwriting with undefined
        setPlacedOrder((prev) => ({
          ...prev,
          orderStatus: data.orderStatus ?? prev?.orderStatus,
          paymentStatus: data.paymentStatus ?? prev?.paymentStatus,
          paymentMethod: data.paymentMethod ?? prev?.paymentMethod,
          pickupOption: data.pickupOption ?? prev?.pickupOption,
          pickupDate: data.pickupDate ?? prev?.pickupDate,
          pickupTime: data.pickupTime ?? prev?.pickupTime,
        }));

        const status = (data.orderStatus || "").toLowerCase();
        const paymentStatus = (data.paymentStatus || "").toLowerCase();
        if (["completed", "ready"].includes(status) || ["void", "refunded"].includes(paymentStatus)) {
          setShowCancelButton(false);
          setCancelDeadline(null);
          sessionStorage.removeItem(cancelDeadlineKey);
        }
      });

      return () => {
        active = false;
        unsubscribe();
      };
    }
  }, [currentPage, placedOrder?.orderId, companyId]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-900 via-purple-800 to-black">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-purple-400 mx-auto mb-4"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Single return with modals always mounted and conditional page rendering
  return (
    <>
      {/* Confirmation Modal */}
      {showConfirmModal && confirmModalData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Confirm Action</h3>
            <p className="text-gray-600 mb-6">{confirmModalData.message}</p>
            <div className="flex gap-3">
              <button
                onClick={confirmModalData.onCancel}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmModalData.onConfirm}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
              >
                Yes, Continue
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Order History Selection Modal */}
      {showOrderHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">Select Order</h3>
              <button
                onClick={() => {
                  setShowOrderHistoryModal(false);
                  setOrderHistoryList([]);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimes size={24} />
              </button>
            </div>
            <p className="text-gray-600 mb-4">You have multiple orders. Please select one:</p>
            <div className="space-y-3">
              {orderHistoryList.map((order) => (
                <button
                  key={order.id}
                  onClick={() => handleSelectOrder(order)}
                  className="w-full bg-gray-50 hover:bg-purple-50 border border-gray-200 hover:border-purple-300 rounded-lg p-4 text-left transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-gray-900">Queue #{order.queueNumber}</p>
                      <p className="text-sm text-gray-600 mt-1">
                        Status: <span className={`font-medium ${
                          order.orderStatus === 'pending' ? 'text-yellow-600' :
                          order.orderStatus === 'preparing' ? 'text-blue-600' :
                          order.orderStatus === 'completed' || order.orderStatus === 'ready' ? 'text-green-600' :
                          'text-gray-600'
                        }`}>
                          {order.orderStatus === 'pending' ? 'Pending' :
                            order.orderStatus === 'preparing' ? 'Preparing' :
                            order.orderStatus === 'completed' || order.orderStatus === 'ready' ? 'Ready' :
                            order.orderStatus}
                        </span>
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        {order.items?.length || 0} item(s)
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-purple-600">
                        {formatPrice(order.totals?.total || 0)}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
      </div>
    )}

    {/* Product detail modal */}
    {selectedProduct && (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center border-b px-6 py-4">
            <h3 className="text-2xl font-bold text-gray-900">{selectedProduct.name}</h3>
            <button
              onClick={closeProductModal}
              className="text-gray-500 hover:text-gray-800 text-2xl leading-none"
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
            <div className="flex justify-center">
              {(() => {
                const productImages = (() => {
                  const imgs = [];
                  if (Array.isArray(selectedProduct.images)) {
                    selectedProduct.images.forEach((img) => {
                      if (!img) return;
                      if (typeof img === "string") {
                        imgs.push(img);
                      } else if (typeof img === "object") {
                        const url = img.url || img.downloadURL || img.src;
                        if (url) imgs.push(url);
                      }
                    });
                  }
                  if (selectedProduct.imageUrl) {
                    imgs.unshift(selectedProduct.imageUrl);
                  }
                  return Array.from(new Set(imgs));
                })();

                const hasImages = productImages.length > 0;
                const activeIndex = hasImages
                  ? Math.min(productImageIndex, productImages.length - 1)
                  : 0;
                const currentImage = hasImages ? productImages[activeIndex] : NoImage;

                if (!hasImages) {
                  return (
                    <div className="w-full max-w-md h-64 bg-gray-100 rounded-xl flex items-center justify-center text-gray-400">
                      No image
                    </div>
                  );
                }

                return (
                  <div className="w-full max-w-md space-y-3">
                    <div className="relative">
                      <img
                        src={currentImage}
                        alt={selectedProduct.name}
                        className="w-full max-h-96 rounded-xl object-contain bg-gray-100"
                        loading="lazy"
                        onError={(e) => {
                          if (e.target.src !== NoImage) {
                            e.target.src = NoImage;
                          }
                        }}
                      />
                      {productImages.length > 1 && hasImages && (
                        <>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setProductImageIndex((prev) =>
                                prev === 0 ? productImages.length - 1 : prev - 1
                              );
                            }}
                            className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 rounded-full p-2 shadow"
                            aria-label="Previous image"
                          >
                            <FaChevronLeft />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setProductImageIndex((prev) =>
                                prev === productImages.length - 1 ? 0 : prev + 1
                              );
                            }}
                            className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 rounded-full p-2 shadow"
                            aria-label="Next image"
                          >
                            <FaChevronRight />
                          </button>
                        </>
                      )}
                    </div>

                    {productImages.length > 1 && (
                      <div className="flex gap-2 overflow-x-auto pb-1">
                        {productImages.map((thumb, idx) => (
                          <button
                            key={`${selectedProduct.id}-thumb-${idx}`}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setProductImageIndex(idx);
                            }}
                            className={`border rounded-lg p-1 bg-white ${
                              idx === activeIndex
                                ? "border-purple-600 shadow"
                                : "border-gray-200 hover:border-purple-400"
                            }`}
                            aria-label={`Preview image ${idx + 1}`}
                          >
                            <img
                              src={thumb}
                              alt={`Preview ${idx + 1}`}
                              className="h-16 w-16 object-contain"
                              loading="lazy"
                              onError={(e) => {
                                if (e.target.src !== NoImage) {
                                  e.target.src = NoImage;
                                }
                              }}
                            />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
            <div className="space-y-4">
              {selectedProduct.type === "bundle" && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-gray-800">Includes</h4>
                  <ul className="text-gray-700 text-sm list-disc list-inside space-y-1 max-h-36 overflow-y-auto pr-1 scrollbar-thin">
                    {(selectedProduct.items || []).map((sub, idx) => (
                      <li key={idx}>
                        {sub.name} × {sub.quantity || 1}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedProduct.desc && selectedProduct.type !== "bundle" && (
                <div className="space-y-1">
                  <h4 className="text-sm font-semibold text-gray-800">Description</h4>
                  <div className="text-gray-700 whitespace-pre-wrap max-h-48 overflow-y-auto pr-1 scrollbar-thin">
                    {selectedProduct.desc}
                  </div>
                </div>
              )}

              <p className="text-xl font-bold text-purple-700">
                {selectedProduct.type === "bundle"
                  ? (() => {
                      const { deltaTotal } = buildBundleConfig(selectedProduct, bundleDraftSelections);
                      return formatPrice(Number(selectedProduct.price || 0) + deltaTotal);
                    })()
                  : formatPrice(getSelectedUnitPrice(selectedProduct, selectedProductOptionValues))}
              </p>

              {selectedProduct.type !== "bundle" && Array.isArray(selectedProduct.options) && selectedProduct.options.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="text-sm font-semibold text-gray-800 mb-3">Options</h4>
                  <div className="space-y-3 max-h-72 overflow-y-auto pr-1 scrollbar-thin">
                    {(() => {
                      const selectionLabels = {};
                      const allSelections = buildSelections(selectedProduct, selectedProductOptionValues);
                      allSelections.forEach((s) => {
                        selectionLabels[s.optKey] = { key: s.valueKey, label: s.valueLabel, available: s.available };
                      });
                      return selectedProduct.options.map((opt, idx) => {
                        const optKey = getOptionKey(opt, idx);
                        const enriched = allSelections.find((s) => s.optKey === optKey);
                        const values = enriched?.values || [];
                        const selectedKey = selectedProductOptionValues[optKey];
                        return (
                          <div key={optKey} className="space-y-1">
                            <div className="text-sm font-medium text-gray-900">{opt.name || opt.label || opt.value || `Option ${idx + 1}`}</div>
                            <div className="flex gap-2 overflow-x-auto max-w-full pb-1 scrollbar-thin">
                              {values.map((val) => {
                                const active = selectedKey === val.key;
                                const variantSelectable = isValueSelectable(
                                  productsMap[selectedProduct.id] || selectedProduct,
                                  optKey,
                                  val.key,
                                  val.label,
                                  selectionLabels
                                );
                                const disabled = val.available !== null && val.available <= 0 || !variantSelectable;
                                return (
                                  <button
                                    key={val.key}
                                    type="button"
                                    onClick={() =>
                                      !disabled &&
                                      setSelectedProductOptionValues((prev) => ({
                                        ...prev,
                                        [optKey]: val.key,
                                      }))
                                    }
                                    className={`px-3 py-2 rounded-lg border text-sm transition flex-shrink-0 ${
                                      disabled
                                        ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : active
                                        ? 'border-purple-600 bg-purple-50 text-purple-700 font-semibold'
                                        : 'border-gray-300 bg-white text-gray-800 hover:border-purple-400'
                                    }`}
                                    disabled={disabled}
                                  >
                                    {val.label}
                                    {(() => {
                                      const productRef = productsMap[selectedProduct.id] || selectedProduct;
                                      const delta = getVariantDeltaForValue(productRef, optKey, val.key, val.label);
                                      if (delta > 0 && Number(val.price || 0) !== 0) return ` (+${formatPrice(delta)})`;
                                      if (delta < 0 && Number(val.price || 0) !== 0) return ` (${formatPrice(delta)})`;
                                      return '';
                                    })()}
                                    {disabled ? ' (Out of stock)' : ''}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}

              {selectedProduct.type === "bundle" && (
                <div className="border-t pt-4 space-y-3">
                  <h4 className="text-sm font-semibold text-gray-800 mb-1">Options</h4>
                  {(selectedProduct.items || []).map((sub) => {
                    const product = productsMap[sub.id];
                    const hasVariants = sub.variantId === ANY_VARIANT_ID || sub.allowVariantChoice;
                    if (!hasVariants || !product?.options?.length) {
                      return null;
                    }
                    const slots = Math.max(1, Number(sub.quantity || 1));
                    const selections = Array.isArray(bundleDraftSelections[sub.id])
                      ? bundleDraftSelections[sub.id]
                      : [];
                    return (
                      <div key={sub.id} className="bg-white border rounded p-3 shadow-sm">
                        <div className="flex justify-between items-center">
                          <div className="font-semibold text-gray-900">{product?.name || sub.name}</div>
                          <div className="text-xs text-gray-500">Qty: {sub.quantity || 1}</div>
                        </div>
                        <div className="mt-2 space-y-3">
                          {Array.from({ length: slots }).map((_, slotIdx) => {
                            const currentVariantId = selections[slotIdx] || '';
                            const currentVariant = product?.variants?.find(
                              (v) => (v.id || v.variantId) === currentVariantId
                            );
                            const currentSelections = {};
                            (currentVariant?.options || []).forEach((o) => {
                              currentSelections[o.group] = o.value;
                            });
                            return (
                              <div key={`${sub.id}-slot-${slotIdx}`} className="border rounded p-2">
                                <div className="text-xs font-semibold text-gray-700 mb-1">Selection {slotIdx + 1}</div>
                                {product.options.map((opt, optIdx) => {
                                  const optKey = opt.name || opt.label || opt.value || `Option ${optIdx + 1}`;
                                  const values = (opt.values || []).map((v, idx) => ({
                                    key: `${optKey}-${idx}`,
                                    label: v.label ?? v.value ?? v,
                                    price: Number(v.price || 0),
                                  }));
                                  const selectedVal = currentSelections[opt.name] || currentSelections[optKey];
                                  return (
                                    <div key={`${optKey}-${slotIdx}`} className="space-y-1">
                                      <div className="text-xs font-medium text-gray-800">{optKey}</div>
                                      <div className="flex gap-2 overflow-x-auto max-w-full pb-1 scrollbar-thin">
                                        {values.map((val) => {
                                          const draftSelections = { ...currentSelections, [opt.name || optKey]: val.label };
                                          const matchVariant = findVariantByOptionLabels(product, draftSelections);
                                          const disabled = !matchVariant || Number(matchVariant.qty || 0) <= 0;
                                          const delta = matchVariant ? computeVariantValueDelta(product, matchVariant) : 0;
                                          const active = selectedVal === val.label;
                                          return (
                                            <button
                                              key={`${val.key}-${slotIdx}`}
                                              type="button"
                                              disabled={disabled}
                                              className={`px-3 py-2 rounded-lg border text-sm transition flex-shrink-0 ${
                                                disabled
                                                  ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                                                  : active
                                                    ? "border-purple-600 bg-purple-50 text-purple-700 font-semibold"
                                                    : "border-gray-300 bg-white text-gray-800 hover:border-purple-400"
                                              }`}
                                              onClick={() => {
                                                if (!matchVariant) return;
                                                setBundleDraftSelections((prev) => {
                                                  const next = { ...prev };
                                                  const arr = Array.isArray(next[sub.id]) ? [...next[sub.id]] : [];
                                                  arr[slotIdx] = matchVariant.id || matchVariant.variantId;
                                                  next[sub.id] = arr;
                                                  return next;
                                                });
                                              }}
                                            >
                                              {val.label}
                                              {delta !== 0 && Number(val.price || 0) !== 0
                                                ? ` (${delta > 0 ? "+" : ""}${formatPrice(delta)})`
                                                : ""}
                                              {disabled ? " (Out of stock)" : ""}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="flex items-center gap-3 pt-2">
                <div className="flex items-center border rounded-lg">
                  <button
                    className="px-3 py-2 text-lg"
                    onClick={() => setSelectedProductQuantity((q) => Math.max(1, q - 1))}
                  >
                    −
                  </button>
                  <span className="px-4">{selectedProductQuantity}</span>
                  <button
                    className="px-3 py-2 text-lg"
                    onClick={() => setSelectedProductQuantity((q) => q + 1)}
                  >
                    +
                  </button>
                </div>
                <button
                  onClick={addSelectedProductToCart}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-lg transition"
                >
                  {selectedProduct.type === "bundle"
                    ? (() => {
                        const { deltaTotal } = buildBundleConfig(selectedProduct, bundleDraftSelections);
                        const price = (Number(selectedProduct.price || 0) + deltaTotal) * selectedProductQuantity;
                        return `Add to basket ${formatPrice(price)}`;
                      })()
                    : `Add to basket ${formatPrice(getSelectedUnitPrice(selectedProduct, selectedProductOptionValues) * selectedProductQuantity)}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )}

      {/* Phone Prompt Modal */}
      {showPhonePrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Enter Phone Number</h3>
            <p className="text-gray-600 mb-4">Please enter your phone number to view order history</p>
            <input
              type="tel"
              value={phonePromptValue}
              onChange={(e) => {
                let phoneValue = e.target.value;
                if (!phoneValue.startsWith("+60")) {
                  phoneValue = "+60";
                }
                const numberPart = phoneValue.substring(3);
                const cleanNumber = numberPart.replace(/\D/g, "");
                if (cleanNumber.length > 0 && cleanNumber[0] === "0") {
                  return;
                }
                if (cleanNumber.length > 10) {
                  return;
                }
                setPhonePromptValue("+60" + cleanNumber);
              }}
              placeholder="+60"
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-purple-500 focus:border-transparent mb-4"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handlePhonePromptSubmit();
                }
              }}
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowPhonePrompt(false);
                  setPhonePromptValue("+60");
                }}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handlePhonePromptSubmit}
                disabled={isSubmittingNumber}
                className={`w-full py-3 rounded-lg font-semibold transition ${
                  isSubmittingNumber
                    ? "bg-purple-400 cursor-wait"
                    : "bg-purple-600 hover:bg-purple-700 text-white"
                }`}
              >
                {isSubmittingNumber ? (
                  <div className="flex items-center justify-center gap-2">
                    <span className="animate-spin border-2 border-white border-t-transparent rounded-full w-5 h-5"></span>
                    <span>Loading...</span>
                  </div>
                ) : (
                  "Submit"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {bundleVariantModalOpen && bundleVariantContext.bundle && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-60 p-4">
          <div className="bg-white text-gray-900 rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b px-6 py-4">
              <h3 className="text-xl font-bold">Choose variants for {bundleVariantContext.bundle.name}</h3>
              <button
                onClick={() => setBundleVariantModalOpen(false)}
                className="text-gray-500 hover:text-gray-800 text-2xl leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="p-6 space-y-4">
              {(bundleVariantContext.bundle.items || [])
                .filter((sub) => sub.variantId === ANY_VARIANT_ID || sub.allowVariantChoice)
                .map((sub) => {
                  const product = productsMap[sub.id];
                  const variants = product?.variants || [];
                  const basePrice = Number(sub.basePrice ?? product?.price ?? sub.price ?? 0);
                  const selectedId = bundleVariantContext.selections[sub.id] || "";
                  const totalVariantQty = Array.isArray(product?.variants)
                    ? product.variants.reduce((s, v) => s + Number(v.qty || 0), 0)
                    : 0;
                  return (
                    <div key={sub.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-semibold text-gray-900">{sub.name || product?.name}</div>
                          <div className="text-sm text-gray-600">Qty in bundle: {sub.quantity || 1}</div>
                        </div>
                        <div className="text-sm text-gray-500">
                          Stock total: {product?.qty ?? totalVariantQty ?? 0}
                        </div>
                      </div>
                      <div className="mt-3 space-y-3">
                        {product?.options?.map((opt, optIdx) => {
                          const optKey = opt.name || opt.label || opt.value || `Option ${optIdx + 1}`;
                          const values = (opt.values || []).map((v, idx) => ({
                            key: `${optKey}-${idx}`,
                            label: v.label ?? v.value ?? v,
                            price: Number(v.price || 0),
                          }));
                          const currentVariant = product?.variants?.find(
                            (v) => (v.id || v.variantId) === selectedId
                          );
                          const currentSelections = {};
                          (currentVariant?.options || []).forEach((o) => {
                            currentSelections[o.group] = o.value;
                          });
                          const selectedVal = currentSelections[opt.name] || currentSelections[optKey];
                          return (
                            <div key={optKey} className="space-y-1">
                              <div className="text-sm font-medium text-gray-800">{optKey}</div>
                              <div className="flex gap-2 overflow-x-auto max-w-full pb-1 scrollbar-thin">
                                {values.map((val) => {
                                  const active = selectedVal === val.label;
                                  const draftSelections = { ...currentSelections, [opt.name || optKey]: val.label };
                                  const matchVariant = findVariantByOptionLabels(product, draftSelections);
                                  const disabled = !matchVariant || Number(matchVariant.qty || 0) <= 0;
                                  const delta = matchVariant ? computeVariantValueDelta(product, matchVariant) : 0;
                                  return (
                                    <button
                                      key={val.key}
                                      type="button"
                                      disabled={disabled}
                                      className={`px-3 py-2 rounded-lg border text-sm transition flex-shrink-0 ${
                                        disabled
                                          ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                                          : active
                                            ? "border-purple-600 bg-purple-50 text-purple-700 font-semibold"
                                            : "border-gray-300 bg-white text-gray-800 hover:border-purple-400"
                                      }`}
                                      onClick={() => {
                                        if (!matchVariant) return;
                                        setBundleVariantContext((prev) => ({
                                          ...prev,
                                          selections: { ...prev.selections, [sub.id]: matchVariant.id || matchVariant.variantId },
                                        }));
                                      }}
                                    >
                                      {val.label}
                                      {delta !== 0 && ` (${delta > 0 ? '+' : ''}${formatPrice(delta)})`}
                                      {disabled ? " (Out of stock)" : ""}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                        {(!product?.options || product.options.length === 0) && (
                          <div className="text-xs text-red-500">No variants available for this item.</div>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
            <div className="flex justify-end gap-2 border-t px-6 py-4 bg-gray-50">
              <button
                onClick={() => setBundleVariantModalOpen(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmBundleVariants}
                className="px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700"
              >
                Add Bundle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PAGE 1: Welcome Page */}
      {currentPage === 1 && (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-black flex flex-col">
        <div className="flex-grow flex items-center justify-center px-4 py-8">
          <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full">
            {/* Company Logo & Name */}
            <div className="text-center mb-8">
              {companyLogo && (
                <img
                  src={companyLogo}
                  alt={companyName}
                  className="h-24 w-24 object-contain mx-auto mb-4 rounded-full shadow-lg"
                />
              )}
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {companyName}
              </h1>
              <p className="text-gray-600">Online Ordering</p>
            </div>

            {/* Check if online ordering is disabled */}
            {!onlineOrderingLive ? (
              <div className="text-center py-8">
                <div className="mb-4">
                  <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Online Ordering Unavailable</h2>
                <p className="text-gray-600 mb-4">
                  {offlineReason || `${companyName} is not accepting online orders at the moment.`}
                </p>
                {openingTime && closingTime && (
                  <p className="text-sm text-gray-500">
                    Hours: {openingTime} - {closingTime}
                  </p>
                )}
              </div>
            ) : (
              <>
            {/* Customer Info Form */}
            <div className="space-y-4 mb-6">
              <input
                type="text"
                name="name"
                placeholder="Your Name *"
                value={customerInfo.name}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
              { nameError && <p className="text-red-600 text-sm mt-1">{nameError}</p> }
              <div>
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number *"
                  value={customerInfo.phone || "+60"}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
                {phoneError && (
                  <p className="text-red-600 text-sm mt-1">{phoneError}</p>
                )}
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  Fulfillment Method
                </label>
                <div className={`grid gap-2 ${availableFulfillmentCount > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
                  {pickupEnabled && (
                    <button
                      type="button"
                      onClick={() =>
                        setCustomerInfo((prev) => ({
                          ...prev,
                          fulfillmentType: "pickup",
                          deliveryAddress: "",
                          orderType: prev.orderType === "delivery" ? "" : prev.orderType,
                        }))
                      }
                      className={`w-full py-3 px-4 rounded-lg border ${
                        customerInfo.fulfillmentType === "pickup"
                          ? "border-purple-600 bg-purple-50 text-purple-700 font-semibold"
                          : "border-gray-300 bg-white text-gray-700"
                      }`}
                    >
                      Pickup
                    </button>
                  )}
                  {deliveryEnabled && (
                    <button
                      type="button"
                      onClick={() =>
                        setCustomerInfo((prev) => ({
                          ...prev,
                          fulfillmentType: "delivery",
                          orderType: "delivery",
                        }))
                      }
                      className={`w-full py-3 px-4 rounded-lg border ${
                        customerInfo.fulfillmentType === "delivery"
                          ? "border-purple-600 bg-purple-50 text-purple-700 font-semibold"
                          : "border-gray-300 bg-white text-gray-700"
                      }`}
                    >
                      Delivery
                    </button>
                  )}
                </div>
              </div>

              {customerInfo.fulfillmentType === "delivery" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Delivery Address *
                  </label>
                  <textarea
                    name="deliveryAddress"
                    placeholder="Enter your UTP delivery address"
                    value={customerInfo.deliveryAddress}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              )}

              {/* Pickup scheduling */}
              <div className="space-y-3">
                <div className={`grid gap-2 ${preOrderEnabled ? "grid-cols-3" : "grid-cols-2"}`}>
                  <button
                    type="button"
                    onClick={() => setCustomerInfo((prev) => ({ ...prev, pickupOption: "now" }))}
                    className={`w-full py-3 px-4 rounded-lg border ${
                      customerInfo.pickupOption === "now"
                        ? "border-purple-600 bg-purple-50 text-purple-700 font-semibold"
                        : "border-gray-300 bg-white text-gray-700"
                    }`}
                  >
                    {customerInfo.fulfillmentType === "delivery" ? "Deliver Now" : "Pickup Now"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setCustomerInfo((prev) => ({ ...prev, pickupOption: "later" }))}
                    className={`w-full py-3 px-4 rounded-lg border ${
                      customerInfo.pickupOption === "later"
                        ? "border-purple-600 bg-purple-50 text-purple-700 font-semibold"
                        : "border-gray-300 bg-white text-gray-700"
                    }`}
                  >
                    {customerInfo.fulfillmentType === "delivery" ? "Deliver Later" : "Pickup Later"}
                  </button>
                  {preOrderEnabled && (
                    <button
                      type="button"
                      onClick={() => {
                        setPickupDateError("");
                        setPickupTimeError("");
                        setCustomerInfo((prev) => ({
                          ...prev,
                          pickupOption: "preorder",
                          pickupDate: "",
                          pickupTime: "",
                        }));
                      }}
                      className={`w-full py-3 px-4 rounded-lg border ${
                        customerInfo.pickupOption === "preorder"
                          ? "border-purple-600 bg-purple-50 text-purple-700 font-semibold"
                          : "border-gray-300 bg-white text-gray-700"
                      }`}
                    >
                      Pre-order
                    </button>
                  )}
                </div>

                {customerInfo.pickupOption === "later" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {customerInfo.fulfillmentType === "delivery" ? "Delivery Date *" : "Pickup Date *"}
                      </label>
                      <input
                        type="date"
                        name="pickupDate"
                        value={customerInfo.pickupDate}
                        onChange={handleInputChange}
                        min={getTodayStr()}
                        max={getDateStrDaysFromToday(5)}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        required
                      />
                      {pickupDateError && <p className="text-red-600 text-sm mt-1">{pickupDateError}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {customerInfo.fulfillmentType === "delivery" ? "Delivery Time *" : "Pickup Time *"}
                        </label>
                        <input
                          type="time"
                          name="pickupTime"
                          value={customerInfo.pickupTime}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        required
                        min={pickupTimeMin}
                        max={pickupTimeMax}
                      />
                      {pickupTimeError && <p className="text-red-600 text-sm mt-1">{pickupTimeError}</p>}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Privacy Policy */}
            <div className="mb-6">
              <div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={privacyAccepted}
                    onChange={(e) => {
                      setPrivacyAccepted(e.target.checked);
                      if (e.target.checked) setPrivacyError(false);
                    }}
                    className="w-4 h-4"
                  />
                  I agree to the privacy policy
                </label>
                {privacyError && (
                  <p className="text-red-600 text-sm mt-1 ml-6">Please agree to privacy & policy</p>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleStartOrder}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 shadow-lg"
              >
                Start Order
              </button>
              <button
                onClick={fetchOrderHistory}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
              >
                <FaHistory /> Order History
              </button>
            </div>
            </>
            )}
          </div>
        </div>

        <footer className="bg-black bg-opacity-50 text-white text-center py-4 mt-8">
        <p className="text-sm">Powered by CashMate</p>
      </footer>
      </div>
      )}

      {/* PAGE 3: Order Confirmation */}
      {currentPage === 3 && placedOrder && (
        <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-black flex items-center justify-center px-4 py-8">
          <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-2xl w-full">

            {/* compute statuses from placedOrder.orderStatus */}
            {(() => {
              const orderStatus = placedOrder.orderStatus; // may be undefined -> treat as not ready
              const isOrdering = true; // always filled purple
              const isPreparing = orderStatus === "pending"; // only pending shows preparing
              const isReady = orderStatus === "completed"; // only completed shows ready
              return (
                <>
                  {/* Success Icon */}
                  <div className="text-center mb-6">
                    <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-green-100 mb-4">
                      <FaCheckCircle className="h-12 w-12 text-green-600" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">
                      We've Received Your Order!
                    </h2>
                  </div>

                  {/* Progress Indicator */}
                  <div className="mb-8">
                    <div className="flex items-center justify-between relative">
                      {/* Ordering (always filled purple) */}
                      <div className="flex flex-col items-center z-10 flex-1">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center bg-purple-600 text-white font-bold mb-2">
                          1
                        </div>
                        <span className="text-xs sm:text-sm font-medium text-gray-700 text-center">Ordering</span>
                      </div>

                      {/* Line Ordering → Preparing */}
                      <div className="absolute top-5 sm:top-6 left-0 right-0 h-1 bg-gray-300 -z-0" style={{ marginLeft: '10%', marginRight: '10%' }}>
                        <div className={`h-full bg-purple-600 transition-all duration-500 ${isPreparing || isReady ? 'w-1/2' : 'w-0'}`} />
                      </div>

                      {/* Preparing */}
                      <div className="flex flex-col items-center z-10 flex-1">
                        <div className={`${isPreparing ? 'bg-purple-600 animate-[heartbeat_1.5s_ease-in-out_infinite]' : (isReady ? 'bg-purple-600' : 'bg-gray-300')} w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-white font-bold mb-2`}>
                          2
                        </div>
                        <span className="text-xs sm:text-sm font-medium text-gray-700 text-center">Preparing</span>
                      </div>

                      {/* Line Preparing → Ready */}
                      <div className="absolute top-5 sm:top-6 left-1/2 right-0 h-1 bg-gray-300 -z-0" style={{ marginRight: '10%' }}>
                        <div className={`h-full bg-purple-600 transition-all duration-500 ${isReady ? 'w-full' : 'w-0'}`} />
                      </div>

                      {/* Ready */}
                      <div className="flex flex-col items-center z-10 flex-1">
                        <div className={`${isReady ? 'bg-purple-600 animate-[heartbeat_1.5s_ease-in-out_infinite]' : 'bg-gray-300'} w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-white font-bold mb-2`}>
                          3
                        </div>
                        <span className="text-xs sm:text-sm font-medium text-gray-700 text-center">
                          {placedOrder.fulfillmentType === "delivery" ? "Ready for Delivery" : "Ready to Pickup"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Fulfillment Summary */}
                  <div className="bg-purple-50 border border-purple-100 rounded-lg p-4 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      {(() => {
                        const option = placedOrder.pickupOption;
                        const fulfillment = placedOrder.fulfillmentType || "pickup";
                        const isPreOrder = option === "preorder";
                        const isScheduled = option === "later";
                        const baseLabel = fulfillment === "delivery" ? "Delivery" : "Pickup";
                        const label = isPreOrder ? "Pre-order" : baseLabel;
                        const dateLabel = `${placedOrder.pickupDate || placedOrder.customerInfo?.pickupDate || "—"} ${placedOrder.pickupTime || placedOrder.customerInfo?.pickupTime || ""}`.trim();
                        return (
                          <>
                            <p className="text-xs text-purple-700 uppercase tracking-wide mb-1">{label}</p>
                            <p className="text-lg font-semibold text-purple-900">
                              {isScheduled
                                ? `${label} • ${dateLabel}`
                                : isPreOrder
                                ? "Scheduled"
                                : `${baseLabel} Now`}
                            </p>
                          </>
                        );
                      })()}
                    </div>
                    <div className="sm:text-right">
                      <p className="text-xs text-purple-700 uppercase tracking-wide mb-1">
                        {placedOrder.fulfillmentType === "delivery" ? "Delivery Address" : "Pickup Location"}
                      </p>
                      <p className="text-sm text-purple-900">
                        {placedOrder.fulfillmentType === "delivery"
                          ? (placedOrder.deliveryAddress || placedOrder.customerInfo?.deliveryAddress || "Delivery address not provided")
                          : (companyAddress || "Pickup address not provided")}
                      </p>
                    </div>
                  </div>

                  {/* Queue Number */}
                  <div className="bg-purple-100 rounded-lg p-6 mb-6 text-center">
                    <p className="text-gray-700 mb-2 font-semibold">Queue Number</p>
                    <p className="text-4xl sm:text-5xl font-bold text-purple-600">
                      Queue #{placedOrder.queueNumber || "—"}
                    </p>
                    <p className="text-sm text-gray-600 mt-3">
                      {(() => {
                        const status = (placedOrder.paymentStatus || "unpaid").toLowerCase();
                        const method = (placedOrder.paymentMethod || "").toLowerCase();
                        if (status === "void" || status === "refunded") {
                          return "This order has been voided. Please contact the merchant if needed.";
                        }
                        if (status === "paid" && method.includes("stripe")) {
                          return placedOrder.fulfillmentType === "delivery"
                            ? "Payment completed online. We will use this order for delivery."
                            : "Payment completed online. Use this queue number for pickup.";
                        }
                        if (status === "pending_verification" && method.includes("duitnow")) {
                          return "Your QR payment proof has been uploaded and is waiting for verification.";
                        }
                        if (status === "proof_rejected") {
                          return "Your QR payment proof was rejected. Please pay again using QR or online payment.";
                        }
                        if (method.includes("stripe")) {
                          return "Payment pending. You can pay with (FPX/cards) or switch to Pay with QR.";
                        }
                        return placedOrder.fulfillmentType === "delivery"
                          ? "This order is waiting for QR payment verification before delivery."
                          : "This queue number is used for pickup and QR payment verification.";
                      })()}
                    </p>
                  </div>

                   {/* Payment Details */}
                  <div className="bg-gray-50 rounded-lg p-6 mb-6">
                    <h3 className="font-semibold text-lg mb-3 text-gray-900">Payment Details</h3>
                    <div className="space-y-2 text-gray-700">
                      <p><span className="font-medium">Payment Method:</span> {placedOrder.paymentMethod || "—"}</p>
                      <p>
                        <span className="font-medium">Payment Status:</span>{' '}
                        {(() => {
                      const status = (placedOrder.paymentStatus || "unpaid").toLowerCase();
                      const badgeClass = status === "paid"
                        ? "bg-green-600"
                        : status === "pending" || status === "pending_verification"
                        ? "bg-yellow-500"
                        : status === "void" || status === "refunded"
                        ? "bg-gray-600"
                        : "bg-red-600";
                      return (
                        <span className={`${badgeClass} text-white text-center text-xs px-2 py-0.5 rounded-full`}>
                          {placedOrder.paymentStatus || "unpaid"}
                        </span>
                      );
                    })()}
                  </p>
                </div>
                {placedOrder.paymentProofUrl && (
                  <div className="mt-4 flex flex-col sm:flex-row gap-3">
                    <a
                      href={placedOrder.paymentProofUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 text-center"
                    >
                      View Uploaded Receipt
                    </a>
                  </div>
                )}
                {(() => {
                  const status = (placedOrder.paymentStatus || "unpaid").toLowerCase();
                  const isPaid = status === "paid";
                  const isVoid = status === "void" || status === "refunded";
                  const canRetry = status === "proof_rejected" || (!isPaid && !isVoid && (placedOrder.paymentMethod || "").toLowerCase().includes("stripe"));
                  if (!canRetry) return null;
                      return (
                        <div className="mt-4 space-y-3">
                          {renderQrPaymentBlock()}
                          {qrProofMissing && (
                            <p className="text-sm text-red-600">Upload a payment proof image or PDF to continue with QR payment.</p>
                          )}
                          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                            <button
                              onClick={handleSubmitQrProofForPlacedOrder}
                              disabled={isUploadingPaymentProof || qrProofMissing}
                              className={`w-full sm:w-auto px-4 py-2 rounded-lg text-white font-semibold ${
                                isUploadingPaymentProof || qrProofMissing ? "bg-emerald-400" : "bg-emerald-600 hover:bg-emerald-700"
                              }`}
                            >
                              {isUploadingPaymentProof ? "Uploading..." : "Pay with QR"}
                            </button>
                            {stripePaymentEnabled && (
                              <>
                                <span className="text-sm font-semibold text-gray-500 text-center">OR</span>
                                <button
                                  onClick={() => startStripeCheckout(placedOrder.orderId)}
                                  disabled={isPaying}
                                  className={`w-full sm:w-auto px-4 py-2 rounded-lg text-white font-semibold ${
                                    isPaying ? "bg-purple-400" : "bg-purple-600 hover:bg-purple-700"
                                  }`}
                                >
                                  {isPaying ? "Redirecting..." : "Pay with FPX"}
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Customer Details */}
                  <div className="bg-gray-50 rounded-lg p-6 mb-6">
                    <h3 className="font-semibold text-lg mb-3 text-gray-900">Customer Details</h3>
                    <div className="space-y-2 text-gray-700">
                      <p><span className="font-medium">Name:</span> {placedOrder.customerInfo?.name || placedOrder.customerName}</p>
                      <p><span className="font-medium">Phone:</span> {placedOrder.customerInfo?.phone || placedOrder.customerPhone}</p>
                      <p><span className="font-medium">Ordering Mode:</span> <span className="bg-purple-600 text-white text-center text-xs px-2 py-0.5 rounded-full">
                        {placedOrder.fulfillmentType === "delivery"
                          ? "DELIVERY"
                          : stallType === 'non-fnb' 
                          ? 'PICKUP' 
                          : placedOrder.orderType === 'dineIn' 
                            ? 'DINE IN' 
                            : placedOrder.orderType === 'takeAway' 
                              ? 'TAKE AWAY' 
                              : placedOrder.mode || placedOrder.orderMode || "—"}</span></p>
                      {placedOrder.fulfillmentType === "delivery" && (
                        <p><span className="font-medium">Delivery Address:</span> {placedOrder.deliveryAddress || placedOrder.customerInfo?.deliveryAddress || "—"}</p>
                      )}
                      <p>
                        {(() => {
                          const option = placedOrder.pickupOption;
                          const fulfillment = placedOrder.fulfillmentType || "pickup";
                          const isPreOrder = option === "preorder";
                          const isScheduled = option === "later";
                          const baseLabel = fulfillment === "delivery" ? "Delivery" : "Pickup";
                          const label = isPreOrder ? "Pre-order" : baseLabel;
                          if (isScheduled) {
                            return (
                              <>
                                <span className="font-medium">{label}:</span>{" "}
                                {`${placedOrder.pickupDate || placedOrder.customerInfo?.pickupDate || "—"} ${placedOrder.pickupTime || placedOrder.customerInfo?.pickupTime || ""}`}
                              </>
                            );
                          }
                          if (isPreOrder) {
                            return (
                              <>
                                <span className="font-medium">{label}:</span> Scheduled
                              </>
                            );
                          }
                          return (
                            <>
                              <span className="font-medium">{label}:</span> Now
                            </>
                          );
                        })()}
                      </p>
                    </div>
                  </div>

                  {/* Order Details */}
                  <div className="bg-gray-50 rounded-lg p-6 mb-6">
                    <h3 className="font-semibold text-lg mb-3 text-gray-900">Order Details</h3>
                    <div className="space-y-3">
                      {placedOrder.items?.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">
                              {item.name} × {item.quantity || item.qty}
                            </p>
                            {(() => {
                              const variantLabel = cleanVariantLabel(
                                item.variantName ||
                                formatVariantName(productsMap[item.id], item.variantId, "")
                              );
                              const productRef = productsMap[item.id];
                              const variantObj = productRef?.variants?.find(
                                (v) => (v.id || v.variantId) === item.variantId
                              );
                              const delta =
                                Number(item.variantPriceDelta || 0) ||
                                (variantObj ? computeVariantValueDelta(productRef, variantObj) : 0);
                              return variantLabel ? (
                                <p className="text-sm text-gray-500 font-medium mt-0.5">
                                  Variant: {variantLabel}
                                  {delta ? ` (+${formatPrice(delta * (item.quantity || item.qty || 1))})` : ""}
                                </p>
                              ) : null;
                            })()}
                            <p className="text-sm text-gray-600">
                                  {item.type === "bundle" && Array.isArray(item.items) && (
                                    <ul className="ml-4 mt-1 text-gray-600 text-sm list-disc list-inside space-y-1">
                                      {item.items.map((sub, sidx) => {
                                        const subVariant = cleanVariantLabel(
                                          sub.variantName ||
                                          formatVariantName(productsMap[sub.id], sub.variantId, "")
                                        );
                                        const subProduct = productsMap[sub.id];
                                        const subVariantObj = subProduct?.variants?.find(
                                          (v) => (v.id || v.variantId) === sub.variantId
                                        );
                                        const subDelta =
                                          Number(sub.variantPriceDelta || 0) ||
                                          (subVariantObj ? computeVariantValueDelta(subProduct, subVariantObj) : 0);
                                        return (
                                          <li key={sidx}>
                                            <span className="font-medium text-gray-700">
                                              {sub.name} × {sub.quantity}
                                            </span>
                                        {subVariant && (
                                          <span className="block text-gray-500">
                                            Variant: {subVariant}
                                            {subDelta ? ` (+${formatPrice(subDelta * (sub.quantity || 1))})` : ""}
                                          </span>
                                        )}
                                      </li>
                                    );
                                  })}
                                </ul>
                              )}
                            </p>
                            {item.note && <p className="text-sm text-gray-500 italic">Note: {item.note}</p>}
                          </div>
                          <p className="font-semibold text-purple-600">{formatPrice(item.price * (item.quantity || item.qty))}</p>
                        </div>
                      ))}
                      <div className="border-t pt-3 mt-3">
                        <div className="space-y-2 text-sm mb-3">
                          <div className="flex justify-between">
                            <span>Subtotal:</span>
                            <span>{formatPrice(placedOrder.totals?.subtotal || 0)}</span>
                          </div>
                          {placedOrder.totals?.sst6Amount > 0 && (
                            <div className="flex justify-between text-gray-600">
                              <span>{taxSettings.sst6Label}:</span>
                              <span>{formatPrice(placedOrder.totals.sst6Amount)}</span>
                            </div>
                          )}
                          {placedOrder.totals?.sst8Amount > 0 && (
                            <div className="flex justify-between text-gray-600">
                              <span>{taxSettings.sst8Label}:</span>
                              <span>{formatPrice(placedOrder.totals.sst8Amount)}</span>
                            </div>
                          )}
                          {placedOrder.totals?.serviceTax10Amount > 0 && (
                            <div className="flex justify-between text-gray-600">
                              <span>{taxSettings.serviceTax10Label}:</span>
                              <span>{formatPrice(placedOrder.totals.serviceTax10Amount)}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex justify-between items-center text-lg font-bold border-t pt-2">
                          <span>Total:</span>
                          <span className="text-purple-600">{formatPrice(placedOrder.totals?.total || 0)}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Cancel Button */}
                  {canRenderCancelButton && String(placedOrder.paymentStatus || "").toLowerCase() !== "paid" && (
                    <button
                      onClick={handleCancelOrder}
                      disabled={isCancelling}
                      className={`w-full font-semibold py-3 px-4 rounded-lg transition-colors duration-200 mb-4 ${isCancelling ? "bg-gray-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700 text-white"}`}
                    >
                      {isCancelling ? "Cancelling..." : `Cancel ${cancelCountdown > 0 ? `(${cancelCountdown}s)` : ""}`}
                    </button>
                  )}

                  {publicId &&
                    placedOrder?.orderId &&
                    String(placedOrder.paymentStatus || "").toLowerCase() === "paid" && (
                    <button
                      onClick={() => {
                        const url = `${window.location.origin}/publicReceipts/${placedOrder.orderId}?pid=${publicId}`;
                        window.open(url, "_blank", "noopener");
                      }}
                      className="w-full bg-white border border-purple-300 text-purple-700 font-semibold py-3 px-4 rounded-lg transition-colors duration-200 mb-3 hover:bg-purple-50"
                    >
                      View E-Receipt
                    </button>
                  )}

                  {/* Place Another Order */}
                  <button
                    onClick={() => {
                      setCurrentPage(1);
                      setCart([]);
                      setItemNote({});
                      setPlacedOrder(null);
                      setShowCancelButton(true);
                      setCancelDeadline(null);
                      sessionStorage.removeItem(cancelDeadlineKey);
                      resetPaymentProof();
                      setSelectedPaymentMethod("qr");
                      setCustomerInfo({ name: "", phone: "", fulfillmentType: "pickup", pickupOption: "now", pickupDate: "", pickupTime: "", deliveryAddress: "", orderType: "" });
                    }}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200"
                  >
                    Place Another Order
                  </button>
                </>
              );
            })()}
          </div>
        </div>
      )}

  {/* PAGE 4: No Order Found */}
  {currentPage === 4 && (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-black flex items-center justify-center px-4 py-8">
      <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-2xl w-full text-center">
        <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-red-100 mb-4">
          <FaTimesCircle className="h-12 w-12 text-red-600" />
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-3">No Order Found</h2>
        <p className="text-gray-600 mb-8">
          We couldn’t find any orders for this phone number. <br />
          Please double-check your number or place a new order below.
        </p>
        <button
          onClick={() => {
            setCurrentPage(1);
            setCart([]);
            setItemNote({});
            setPlacedOrder(null);
            setShowCancelButton(true);
            setCancelDeadline(null);
            sessionStorage.removeItem(cancelDeadlineKey);
            resetPaymentProof();
            setSelectedPaymentMethod("qr");
            setCustomerInfo({ name: "", phone: "", fulfillmentType: "pickup", pickupOption: "now", pickupDate: "", pickupTime: "", deliveryAddress: "", orderType: "" });
          }}
          className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200"
        >
          Place a New Order
        </button>
      </div>
    </div>
  )}


      {/* PAGE 2: Menu & Ordering */}
  {currentPage === 2 && (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-black flex flex-col">
      {/* Header */}
      <header className="bg-black bg-opacity-50 shadow-lg p-4 sticky top-0 z-40">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between text-white mb-3">
            {/* Back Button */}
            <button
              onClick={() => setCurrentPage(1)}
              className="text-white hover:text-purple-300 transition"
            >
              <FaArrowLeft size={20} />
            </button>

            {/* Empty space for balance */}
            <div></div>

            {/* Mobile Checkout Button */}
            <button
              onClick={() => setShowMobileCheckout(true)}
              className="lg:hidden bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <FaShoppingCart />
              <span className="text-sm font-semibold">{cart.length}</span>
            </button>
          </div>

          {/* Customer Info & Date/Time */}
          <div className="text-white">
            <p className="text-2xl font-extrabold tracking-wide">
              {companyName}
            </p>
          </div>

          {/* Customer Info & Date/Time */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-white text-sm">
            <div>
              <p>
                <span className="font-medium">Customer:</span>{" "}
                {customerInfo.name} | {customerInfo.phone}
              </p>
              <p className="mt-1">
                {(() => {
                  const option = placedOrder?.pickupOption || customerInfo.pickupOption;
                  const fulfillment = placedOrder?.fulfillmentType || customerInfo.fulfillmentType || "pickup";
                  const dateVal = placedOrder?.pickupDate || placedOrder?.customerInfo?.pickupDate || customerInfo.pickupDate;
                  const timeVal = placedOrder?.pickupTime || placedOrder?.customerInfo?.pickupTime || customerInfo.pickupTime;
                  const isLater = option === "later";
                  const isPreOrder = option === "preorder";
                  const baseLabel = fulfillment === "delivery" ? "Delivery" : "Pickup";
                  const label = isPreOrder ? "Pre-order" : baseLabel;
                  if (isLater) {
                    return (
                      <>
                        <span className="font-medium">{label}:</span>{" "}
                        {`${dateVal || "—"} ${timeVal || ""}`.trim()}
                      </>
                    );
                  }
                  if (isPreOrder) {
                    return (
                      <>
                        <span className="font-medium">{label}:</span> Scheduled
                      </>
                    );
                  }
                  return (
                    <>
                      <span className="font-medium">{label}:</span> Now
                    </>
                  );
                })()}
              </p>
              {customerInfo.fulfillmentType === "delivery" ? (
                <p className="mt-1 text-purple-100">
                  <span className="font-medium">Delivery Address:</span> {customerInfo.deliveryAddress || "Not provided"}
                </p>
              ) : companyAddress && (
                <p className="mt-1 text-purple-100">
                  <span className="font-medium">Pickup Location:</span> {companyAddress}
                </p>
              )}
            </div>
            <div className="text-gray-300 mt-1 sm:mt-0">{currentDateTime}</div>
          </div>
        </div>
      </header>


      {/* Main */}
      <main className="flex-1 w-full px-4 sm:px-6 lg:px-8 py-6 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Menu */}
          <div className="lg:col-span-2">
            {/* Search Bar */}
            <div className="mb-6">
              <div className="relative">
                <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search menu..."
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>


            {/* Filter Bar */}
            <div className="sticky top-0 z-50 mb-6 overflow-x-auto">
              <div className="flex space-x-3 min-w-max pb-2">
                {[
                  "All",
                  "Bundle",
                  ...(
                    productTypes.length > 0
                      ? productTypes.filter((t) => t !== "bundle")
                      : [...new Set(products.map((item) => item.type))].filter((t) => t !== "bundle")
                  ).sort((a, b) => a.localeCompare(b))
                ].map((type) => (
                  <button
                    key={type}
                    onClick={() => {
                      setSelectedType(type);

                      setTimeout(() => {
                        if (type === "All") {
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        } else {
                          const section = document.getElementById(`section-${type}`);
                          if (section) {
                            section.scrollIntoView({ behavior: "smooth", block: "start" });
                          }
                        }
                      }, 150);
                    }}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                      selectedType === type
                        ? "bg-purple-600 text-white shadow-md"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>


            {/* Products by Type */}
            {(() => {
              // 1️⃣ Get all unique types from filtered items
              const allTypes = [...new Set(filteredItems.map((item) => item.type))];
              
              // 2️⃣ Separate bundles and product types
              const hasBundles = allTypes.includes("bundle");
              const otherTypes = allTypes.filter((t) => t !== "bundle").sort((a, b) => a.localeCompare(b));
              
              // 3️⃣ Bundles first, then sorted product types
              const typesToDisplay = hasBundles ? ["bundle", ...otherTypes] : otherTypes;

              // 4️⃣ Render each section by type (only if items exist)
              return typesToDisplay.map((type) => {
                const typeItems = getItemsByType(type);
                if (typeItems.length === 0) return null; // 🧩 Skip empty categories

                return (
                  <div key={type} className="mb-8" id={`section-${type}`}>
                    <h2 className="text-2xl font-bold text-white mb-4 capitalize">{type}</h2>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {typeItems.map((item) => (
                        <div
                          key={item.id}
                          className={`relative bg-white rounded-lg shadow-lg hover:shadow-xl transition p-4 flex flex-col cursor-pointer ${!item.inStock ? 'opacity-50' : ''}`}
                          onClick={() => openProductModal(item)}
                        >
                          {item.imageUrl && (
                            <img
                              src={item.imageUrl}
                              alt={item.name}
                              className="h-40 w-full object-contain rounded-md mb-3 bg-gray-50"
                            />
                          )}
                          {!item.inStock && (
                            <div className="absolute inset-0 bg-white bg-opacity-70 rounded-lg flex items-center justify-center">
                              <span className="text-gray-700 font-semibold">Out of stock</span>
                            </div>
                          )}
                          <h3 className="font-semibold text-lg text-gray-900">{item.name}</h3>

                          {/* 🧾 Show bundle contents */}
                          {item.type === "bundle" && Array.isArray(item.items) && item.items.length > 0 && (
                            <ul className="text-gray-600 text-sm mt-2 list-disc list-inside">
                              {item.items.map((subItem, i) => {
                                const variantLabel =
                                  subItem.variantName ||
                                  formatVariantName(productsMap[subItem.id], subItem.variantId, "");
                                return (
                                  <li key={i}>
                                    {subItem.name}
                                    {variantLabel ? ` (${variantLabel})` : ""} × {subItem.quantity}
                                  </li>
                                );
                              })}
                            </ul>
                          )}

                          {/* Existing description */}
                          {item.description && (
                            <p className="text-gray-600 text-sm mt-1 flex-grow">{item.description}</p>
                          )}

                          <p className="text-purple-600 font-bold text-lg mt-2">
                            {formatPrice(item.price)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              });
            })()}

            {/* Empty State */}
            {filteredItems.length === 0 && (
              <div className="text-center py-12 bg-white rounded-lg shadow">
                <FaBoxOpen className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  No items found
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm
                    ? "Try adjusting your search"
                    : "No menu items available at the moment"}
                </p>
              </div>
            )}
          </div>

          {/* Desktop Checkout Section */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg sticky top-24">
              <div className="p-4 bg-purple-600 text-white rounded-t-lg">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold">Your Order</h2>
                  {/* {cart.length > 0 && (
                    <button
                      onClick={clearSavedData}
                      className="text-xs bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded transition-colors"
                    >
                      Clear Cart
                    </button>
                  )} */}
                </div>
              </div>
              {cart.length > 0 ? (
                <>
                  <div className="p-4 max-h-96 overflow-y-auto">
                  {cart.map((item) => (
                    <div key={`${item.id}-${item.optionsKey || 'base'}`} className="mb-4 pb-4 border-b">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900">
                              {item.name}
                            </h4>
                            <div className="text-sm text-purple-600">
                              {(() => {
                                const variantLabelClean = cleanVariantLabel(
                                  item.variantName || formatVariantName(productsMap[item.id], item.variantId, "")
                                );
                                const productRef = productsMap[item.id];
                                const variantObj = productRef?.variants?.find(
                                  (v) => (v.id || v.variantId) === item.variantId
                                );
                                const delta =
                                  Number(item.variantPriceDelta || 0) ||
                                  (productRef && variantObj ? computeVariantValueDelta(productRef, variantObj) : 0);
                                if (!variantLabelClean) return null;
                                return (
                                  <div className="text-gray-500">
                                    {variantLabelClean}
                                    {delta ? ` (+${formatPrice(delta)})` : ""}
                                  </div>
                                );
                              })()}
                                  {item.type === "bundle" && Array.isArray(item.items) && (
                                    <ul className="mt-1 text-gray-500 list-disc list-inside">
                                      {item.items.map((sub, idx) => {
                                        const variantLabelClean = cleanVariantLabel(
                                          sub.variantName || formatVariantName(productsMap[sub.id], sub.variantId, "")
                                        );
                                          const productRef = productsMap[sub.id];
                                          const variantObj = productRef?.variants?.find((v) => (v.id || v.variantId) === sub.variantId);
                                          const delta = Number(sub.variantPriceDelta || 0) || (productRef ? computeVariantValueDelta(productRef, variantObj) : 0);
                                          const deltaText = delta ? ` (+${formatPrice(delta)})` : "";
                                          return (
                                            <li key={idx}>
                                              {sub.name}
                                              {variantLabelClean ? ` (${variantLabelClean})` : ""}
                                              {deltaText}
                                              × {sub.quantity}
                                            </li>
                                          );
                                        })}
                                      </ul>
                                    )}
                                  <p>{formatPrice(item.price)} × {item.quantity}</p>
                              {Array.isArray(item.selectedOptions) && item.selectedOptions.length > 0 && (
                                <div className="mt-1 text-xs text-gray-500 space-y-1">
                                  {item.selectedOptions.map((opt, idx) => (
                                    <div key={idx}>
                                      <span className="font-semibold">{opt.optionLabel || opt.optKey}:</span>{" "}
                                      <span>{opt.valueLabel || opt.valueKey}</span>
                                      {opt.price ? ` (+${formatPrice(opt.price)}) ` : " "}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                          onClick={() =>
                            updateQuantity(item.id, item.quantity - 1, item.optionsKey || "")
                          }
                              className="w-8 h-8 bg-gray-200 hover:bg-gray-300 rounded-full flex items-center justify-center"
                            >
                              -
                            </button>
                            <span className="font-semibold">
                              {item.quantity}
                            </span>
                          <button
                            onClick={() =>
                              updateQuantity(item.id, item.quantity + 1, item.optionsKey || "")
                            }
                            className="w-8 h-8 bg-gray-200 hover:bg-gray-300 rounded-full flex items-center justify-center"
                          >
                            +
                          </button>
                            <button
                              onClick={() => removeFromCart(item.id, item.optionsKey || "")}
                              className="ml-2 text-red-500 hover:text-red-700"
                            >
                              <FaTimes />
                            </button>
                          </div>
                        </div>
                        <textarea
                          placeholder="Add note for this item..."
                          value={itemNote[getNoteKey(item)] || ""}
                          onChange={(e) =>
                            handleItemNoteChange(getNoteKey(item), e.target.value)
                          }
                          className="w-full text-sm border rounded px-2 py-1 mt-2"
                          rows="2"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="p-4 border-t">
                    <div className="space-y-2 text-sm mb-3">
                      <div className="flex justify-between">
                        <span>Subtotal:</span>
                        <span>{formatPrice(subtotal)}</span>
                      </div>
                      {taxSettings.sst6Enabled && (
                        <div className="flex justify-between text-gray-600">
                          <span>{taxSettings.sst6Label}:</span>
                          <span>{formatPrice(sst6Amount)}</span>
                        </div>
                      )}
                      {taxSettings.sst8Enabled && (
                        <div className="flex justify-between text-gray-600">
                          <span>{taxSettings.sst8Label}:</span>
                          <span>{formatPrice(sst8Amount)}</span>
                        </div>
                      )}
                    {taxSettings.serviceTax10Enabled && (
                      <div className="flex justify-between text-gray-600">
                        <span>{taxSettings.serviceTax10Label}:</span>
                        <span>{formatPrice(serviceTax10Amount)}</span>
                      </div>
                    )}
                    {selectedDiscount && (
                      <div className="flex justify-between text-gray-600">
                        <span>Discount:</span>
                        <span>-{formatPrice(discountAmount)}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex justify-between py-2 text-lg font-bold border-t">
                    <span>Total:</span>
                    <span className="text-purple-600">
                      {formatPrice(total)}
                    </span>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Discount Code
                    </label>
                    <div className="flex gap-2">
                      <input
                        className="w-full border rounded-lg px-3 py-2 bg-gray-50"
                        placeholder="Enter discount code"
                        value={discountCodeInput}
                        onChange={(e) => setDiscountCodeInput(e.target.value.toUpperCase())}
                      />
                      <button
                        className="px-3 py-2 bg-purple-600 rounded text-white hover:bg-purple-700"
                        onClick={() => {
                          const code = discountCodeInput.trim().toUpperCase();
                          if (!code) {
                            setDiscountError('Enter a code');
                            return;
                          }
                          const match = discountOptions.find((d) => {
                            const codes = d.codes || (d.code ? [d.code] : []);
                            return codes.map((c) => c.toUpperCase()).includes(code);
                          });
                          if (!match) {
                            setDiscountError('Code not valid');
                            setSelectedDiscount(null);
                            setAppliedCode('');
                            return;
                          }
                          if (!isDiscountWithinValidity(match)) {
                            setDiscountError('Code expired');
                            setSelectedDiscount(null);
                            setAppliedCode('');
                            return;
                          }
                          const codes = match.codes || (match.code ? [match.code] : []);
                          const redeemedList = (match.redeemedCodes || []).map((c) => c.toUpperCase());
                          if (match.generated) {
                            if (!codes.map((c) => c.toUpperCase()).includes(code)) {
                              setDiscountError('Code not valid');
                              setSelectedDiscount(null);
                              setAppliedCode('');
                              return;
                            }
                            if (redeemedList.includes(code)) {
                              setDiscountError('Code already redeemed');
                              setSelectedDiscount(null);
                              setAppliedCode('');
                              return;
                            }
                          } else {
                            const totalAllowed = Number(match.voucherCount || codes.length || 0);
                            const redeemedCount = (match.redeemedCodes || []).length;
                            if (totalAllowed && redeemedCount >= totalAllowed) {
                              setDiscountError('Code fully redeemed');
                              setSelectedDiscount(null);
                              setAppliedCode('');
                              return;
                            }
                            if (codes.length > 0 && !codes.map((c) => c.toUpperCase()).includes(code)) {
                              setDiscountError('Code not valid');
                              setSelectedDiscount(null);
                              setAppliedCode('');
                              return;
                            }
                          }
                          setSelectedDiscount({ ...match, appliedCode: code });
                          setAppliedCode(code);
                          setDiscountError('');
                        }}
                      >
                        Apply
                      </button>
                      <button
                        className="px-3 py-2 bg-gray-200 rounded text-gray-700 hover:bg-gray-300"
                        onClick={() => {
                          setSelectedDiscount(null);
                          setAppliedCode('');
                          setDiscountError('');
                          setDiscountCodeInput('');
                        }}
                      >
                        Clear
                      </button>
                    </div>
                    {discountError && <p className="text-red-500 text-xs mt-1">{discountError}</p>}
                    {appliedCode && selectedDiscount && (
                      <p className="text-green-600 text-xs mt-1">Applied {selectedDiscount.name} ({appliedCode})</p>
                    )}
                  </div>
                  {/* Ordering mode removed: pickup handled via pickup options */}
                    
                    {/* Order Type Selection - Only for F&B stall type */}
                    {stallType === 'fnb' && customerInfo.fulfillmentType !== "delivery" && (
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Type of Order <span className="text-red-500">*</span>
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => setCustomerInfo(prev => ({ ...prev, orderType: 'dineIn' }))}
                            className={`p-3 rounded-lg border-2 transition-all ${
                              customerInfo.orderType === 'dineIn'
                                ? 'border-purple-500 bg-purple-50 text-purple-700'
                                : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                            }`}
                          >
                            <div className="font-medium">Dine In</div>
                            <div className="text-xs text-gray-500 mt-1">Eat at restaurant</div>
                          </button>
                          <button
                            type="button"
                            onClick={() => setCustomerInfo(prev => ({ ...prev, orderType: 'takeAway' }))}
                            className={`p-3 rounded-lg border-2 transition-all ${
                              customerInfo.orderType === 'takeAway'
                                ? 'border-purple-500 bg-purple-50 text-purple-700'
                                : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                            }`}
                          >
                            <div className="font-medium">Take Away</div>
                            <div className="text-xs text-gray-500 mt-1">Take to go</div>
                          </button>
                        </div>
                        {!customerInfo.orderType && (
                          <p className="text-red-500 text-sm mt-2">Please select order type to continue</p>
                        )}
                      </div>
                    )}
                    
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Payment Method
                      </label>
                    <select
                      className="w-full border rounded-lg px-3 py-2 bg-gray-50"
                      value={selectedPaymentMethod}
                      onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                    >
                      {paymentOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {!stripeAllowedForTotal && stripePaymentEnabled && (
                      <p className="text-xs text-gray-500 mt-2">
                        Online payment is available for orders RM2 and above.
                      </p>
                    )}
                  </div>
                    {selectedPaymentMethod === "qr" && renderQrPaymentBlock()}
                    {qrProofMissing && (
                      <p className="mt-3 text-sm text-red-600">Upload a payment proof image or PDF to place the order.</p>
                    )}
                    <button
                      onClick={handleSubmitOrder}
                      disabled={placeOrderDisabled}
                      className={`w-full mt-4 py-3 rounded-lg text-white font-semibold transition ${
                        placeOrderDisabled
                          ? "bg-purple-400"
                          : "bg-purple-600 hover:bg-purple-700"
                      }`}
                    >
                      {isSubmitting ? "Processing..." : "Place Order"}
                    </button>
                  </div>
                </>
              ) : (
                <div className="p-6 text-center">
                  <FaShoppingCart className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-2 text-gray-600">Your cart is empty</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Mobile Checkout Modal */}
      {showMobileCheckout && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 lg:hidden">
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 bg-purple-600 text-white rounded-t-2xl flex justify-between items-center">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-semibold">Your Order</h2>
                {/* {cart.length > 0 && (
                  <button
                    onClick={clearSavedData}
                    className="text-xs bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded transition-colors"
                  >
                    Clear
                  </button>
                )} */}
              </div>
              <button
                onClick={() => setShowMobileCheckout(false)}
                className="text-white"
              >
                <FaTimes size={24} />
              </button>
            </div>
            {cart.length > 0 ? (
              <>
                <div className="p-4">
                  {cart.map((item) => (
                    <div key={`${item.id}-${item.optionsKey || 'base'}`} className="mb-4 pb-4 border-b">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">
                            {item.name}
                          </h4>
<div className="text-sm text-purple-600">
  {(() => {
    const variantLabelClean = cleanVariantLabel(
      item.variantName || formatVariantName(productsMap[item.id], item.variantId, "")
    );
    const productRef = productsMap[item.id];
    const variantObj = productRef?.variants?.find((v) => (v.id || v.variantId) === item.variantId);
    const delta =
      Number(item.variantPriceDelta || 0) ||
      (productRef && variantObj ? computeVariantValueDelta(productRef, variantObj) : 0);
    if (!variantLabelClean) return null;
    return (
      <div className="text-gray-500">
        {variantLabelClean}
        {delta ? ` (+${formatPrice(delta)})` : ""}
      </div>
    );
  })()}
  {item.type === "bundle" && Array.isArray(item.items) && (
    <ul className="mt-1 text-gray-500 list-disc list-inside">
      {item.items.map((sub, idx) => {
        const variantLabelClean = cleanVariantLabel(
          sub.variantName || formatVariantName(productsMap[sub.id], sub.variantId, "")
        );
        const productRef = productsMap[sub.id];
        const variantObj = productRef?.variants?.find((v) => (v.id || v.variantId) === sub.variantId);
        const delta = Number(sub.variantPriceDelta || 0) || (productRef && variantObj ? computeVariantValueDelta(productRef, variantObj) : 0);
        return (
          <li key={idx}>
            {sub.name}
            {variantLabelClean ? ` (${variantLabelClean})` : ""}
            {delta ? ` (+${formatPrice(delta)})` : ""} × {sub.quantity}
          </li>
        );
      })}
    </ul>
  )}
  <p>{formatPrice(item.price)} × {item.quantity}</p>
  {Array.isArray(item.selectedOptions) && item.selectedOptions.length > 0 && (
    <div className="mt-1 text-xs text-gray-500 space-y-1">
      {item.selectedOptions.map((opt, idx) => (
        <div key={idx}>
          <span className="font-semibold">{opt.optionLabel || opt.optKey}:</span>{" "}
          <span>{opt.valueLabel || opt.valueKey}</span>
          {opt.price ? ` (+${formatPrice(opt.price)})` : ""}
        </div>
      ))}
    </div>
  )}
</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() =>
                              updateQuantity(item.id, item.quantity - 1, item.optionsKey || "")
                            }
                            className="w-8 h-8 bg-gray-200 hover:bg-gray-300 rounded-full"
                          >
                            -
                          </button>
                          <span className="font-semibold">{item.quantity}</span>
                          <button
                            onClick={() =>
                              updateQuantity(item.id, item.quantity + 1, item.optionsKey || "")
                            }
                            className="w-8 h-8 bg-gray-200 hover:bg-gray-300 rounded-full"
                          >
                            +
                          </button>
                          <button
                            onClick={() => removeFromCart(item.id, item.optionsKey || "")}
                            className="ml-2 text-red-500"
                          >
                            <FaTimes />
                          </button>
                        </div>
                      </div>
                      <textarea
                        placeholder="Add note for this item..."
                        value={itemNote[getNoteKey(item)] || ""}
                        onChange={(e) =>
                          handleItemNoteChange(getNoteKey(item), e.target.value)
                        }
                        className="w-full text-sm border rounded px-2 py-1 mt-2"
                        rows="2"
                      />
                    </div>
                  ))}
                </div>
                <div className="p-4 border-t">
                  <div className="space-y-2 text-sm mb-3">
                    <div className="flex justify-between">
                      <span>Subtotal:</span>
                      <span>{formatPrice(subtotal)}</span>
                    </div>
                    {taxSettings.sst6Enabled && (
                      <div className="flex justify-between text-gray-600">
                        <span>{taxSettings.sst6Label}:</span>
                        <span>{formatPrice(sst6Amount)}</span>
                      </div>
                    )}
                    {taxSettings.sst8Enabled && (
                      <div className="flex justify-between text-gray-600">
                        <span>{taxSettings.sst8Label}:</span>
                        <span>{formatPrice(sst8Amount)}</span>
                      </div>
                    )}
                    {taxSettings.serviceTax10Enabled && (
                      <div className="flex justify-between text-gray-600">
                        <span>{taxSettings.serviceTax10Label}:</span>
                        <span>{formatPrice(serviceTax10Amount)}</span>
                      </div>
                    )}
                    {selectedDiscount && (
                      <div className="flex justify-between text-gray-600">
                        <span>Discount:</span>
                        <span>-{formatPrice(discountAmount)}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex justify-between py-2 text-lg font-bold border-t">
                    <span>Total:</span>
                    <span className="text-purple-600">
                      {formatPrice(total)}
                    </span>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Discount Code
                    </label>
                    <div className="flex gap-2">
                      <input
                        className="w-full border rounded-lg px-3 py-2 bg-gray-50"
                        placeholder="Enter discount code"
                        value={discountCodeInput}
                        onChange={(e) => setDiscountCodeInput(e.target.value.toUpperCase())}
                      />
                      <button
                        className="px-3 py-2 bg-purple-600 rounded text-white hover:bg-purple-700"
                        onClick={() => {
                          const code = discountCodeInput.trim().toUpperCase();
                          if (!code) {
                            setDiscountError('Enter a code');
                            return;
                          }
                          const match = discountOptions.find((d) => {
                            const codes = d.codes || (d.code ? [d.code] : []);
                            return codes.map((c) => c.toUpperCase()).includes(code);
                          });
                          if (!match) {
                            setDiscountError('Code not valid');
                            setSelectedDiscount(null);
                            setAppliedCode('');
                            return;
                          }
                          if (!isDiscountWithinValidity(match)) {
                            setDiscountError('Code expired');
                            setSelectedDiscount(null);
                            setAppliedCode('');
                            return;
                          }
                          const codes = match.codes || (match.code ? [match.code] : []);
                          const redeemedList = (match.redeemedCodes || []).map((c) => c.toUpperCase());
                          if (match.generated) {
                            if (!codes.map((c) => c.toUpperCase()).includes(code)) {
                              setDiscountError('Code not valid');
                              setSelectedDiscount(null);
                              setAppliedCode('');
                              return;
                            }
                            if (redeemedList.includes(code)) {
                              setDiscountError('Code already redeemed');
                              setSelectedDiscount(null);
                              setAppliedCode('');
                              return;
                            }
                          } else {
                            const totalAllowed = Number(match.voucherCount || codes.length || 0);
                            const redeemedCount = (match.redeemedCodes || []).length;
                            if (totalAllowed && redeemedCount >= totalAllowed) {
                              setDiscountError('Code fully redeemed');
                              setSelectedDiscount(null);
                              setAppliedCode('');
                              return;
                            }
                            if (codes.length > 0 && !codes.map((c) => c.toUpperCase()).includes(code)) {
                              setDiscountError('Code not valid');
                              setSelectedDiscount(null);
                              setAppliedCode('');
                              return;
                            }
                          }
                          setSelectedDiscount({ ...match, appliedCode: code });
                          setAppliedCode(code);
                          setDiscountError('');
                        }}
                      >
                        Apply
                      </button>
                      <button
                        className="px-3 py-2 bg-gray-200 rounded text-gray-700 hover:bg-gray-300"
                        onClick={() => {
                          setSelectedDiscount(null);
                          setAppliedCode('');
                          setDiscountError('');
                          setDiscountCodeInput('');
                        }}
                      >
                        Clear
                      </button>
                    </div>
                    {discountError && <p className="text-red-500 text-xs mt-1">{discountError}</p>}
                    {appliedCode && selectedDiscount && (
                      <p className="text-green-600 text-xs mt-1">Applied {selectedDiscount.name} ({appliedCode})</p>
                    )}
                  </div>
                  {/* Ordering mode removed: pickup handled via pickup options */}
                  
                  {/* Order Type Selection - Only for F&B stall type */}
                  {stallType === 'fnb' && customerInfo.fulfillmentType !== "delivery" && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Type of Order <span className="text-red-500">*</span>
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setCustomerInfo(prev => ({ ...prev, orderType: 'dineIn' }))}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            customerInfo.orderType === 'dineIn'
                              ? 'border-purple-500 bg-purple-50 text-purple-700'
                              : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                          }`}
                        >
                          <div className="font-medium">Dine In</div>
                          <div className="text-xs text-gray-500 mt-1">Eat at restaurant</div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setCustomerInfo(prev => ({ ...prev, orderType: 'takeAway' }))}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            customerInfo.orderType === 'takeAway'
                              ? 'border-purple-500 bg-purple-50 text-purple-700'
                              : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                          }`}
                        >
                          <div className="font-medium">Take Away</div>
                          <div className="text-xs text-gray-500 mt-1">Take to go</div>
                        </button>
                      </div>
                      {!customerInfo.orderType && (
                        <p className="text-red-500 text-sm mt-2">Please select order type to continue</p>
                      )}
                    </div>
                  )}
                  
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Method
                    </label>
                    <select
                      className="w-full border rounded-lg px-3 py-2 bg-gray-50"
                      value={selectedPaymentMethod}
                      onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                    >
                      {paymentOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {!stripeAllowedForTotal && stripePaymentEnabled && (
                      <p className="text-xs text-gray-500 mt-2">
                        Online payment is available for orders RM2 and above.
                      </p>
                    )}
                  </div>
                  {selectedPaymentMethod === "qr" && renderQrPaymentBlock()}
                  {qrProofMissing && (
                    <p className="mt-3 text-sm text-red-600">Upload a payment proof image or PDF to place the order.</p>
                  )}
                  <button
                    onClick={handleSubmitOrder}
                    disabled={placeOrderDisabled}
                    className={`w-full mt-4 py-3 rounded-lg text-white font-semibold transition ${
                      placeOrderDisabled
                        ? "bg-purple-400"
                        : "bg-purple-600 hover:bg-purple-700"
                    }`}
                  >
                    {isSubmitting ? "Processing..." : "Place Order"}
                  </button>
                </div>
              </>
            ) : (
              <div className="p-6 text-center">
                <FaShoppingCart className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-gray-600">Your cart is empty</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-black bg-opacity-50 text-white text-center py-4 mt-auto">
        <p className="text-sm">Powered by CashMate</p>
      </footer>

      <ToastContainer
        position="top-center"
        autoClose={1000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        pauseOnHover
        draggable
        theme="colored"
      />

    </div>
      )}
    </>
  );
};

export default OnlineOrdering;
